import { createHash } from "node:crypto";

import { extractFounderLoopItems, type FounderLoopImport, type FounderLoopItem } from "./self-audit.js";
import {
  buildWorkGraphInvestigation,
  type RawEvent,
  type WorkGraphInvestigationV2,
} from "./work-graph-investigation.js";

export const WORK_GRAPH_SCHEMA_VERSION = "2.0.0" as const;
export const WORK_GRAPH_FINGERPRINT_VERSION = "wgf_v1" as const;
export const WORK_GRAPH_EXTRACTION_SCHEMA_VERSION = "2.0.0.investigation" as const;

export const WORK_GRAPH_FINDING_TYPES = [
  "action",
  "decision",
  "artifact",
  "blocker",
  "person",
  "business",
  "product_surface",
  "goal",
  "outcome",
  "initiative_candidate",
  "missed_orchestration_opportunity",
] as const;

export type WorkGraphFindingType = (typeof WORK_GRAPH_FINDING_TYPES)[number];

export type WorkGraphSourceClient =
  | "codex"
  | "claude"
  | "claude-code"
  | "cursor"
  | "opencode"
  | "goose"
  | "openclaw"
  | "slack"
  | "mcp"
  | "orgx_runtime_hook"
  | "github"
  | "linear"
  | "gmail"
  | "calendar"
  | "notion"
  | "docs"
  | "manual"
  | "wizard"
  | "api"
  | "unknown";

export type WorkGraphFinalState = "completed" | "blocked" | "in_progress" | "unknown";

export interface WorkGraphWorkspaceRef {
  id: string;
  name: string;
}

export interface WorkGraphSourceCoverage {
  connected: string[];
  missing: string[];
  mcpObserved: boolean;
  orgxObserved: boolean;
  orgxMcpCalled: boolean;
  skillOnlySignal: boolean;
  coverage_score?: number;
  manifests?: WorkGraphSourceCoverageManifest[];
  notes?: string[];
}

export interface WorkGraphSourceCoverageManifest {
  source_client: WorkGraphSourceClient;
  source_label: string;
  status: "connected" | "partial" | "missing";
  searched_sources: string[];
  searched_session_count: number;
  skipped_session_count: number;
  query_count: number;
  finding_count: number;
  confidence: number;
  notes: string[];
}

export interface WorkGraphSourceEvent {
  schema_version: typeof WORK_GRAPH_SCHEMA_VERSION;
  source_client: WorkGraphSourceClient;
  source_id: string;
  source_label: string;
  event_type: "session_excerpt" | "client_extraction" | "tool_signal" | "source_coverage";
  text: string;
  evidence_ref: string;
  metadata: Record<string, unknown>;
}

export type WorkGraphAttributionKind =
  | "action"
  | "decision"
  | "artifact"
  | "person"
  | "agent"
  | "tool"
  | "business"
  | "product_surface"
  | "goal"
  | "source";

export type WorkGraphRedactionState = "public_summary" | "private" | "redacted";
export type WorkGraphReviewState =
  | "unreviewed"
  | "confirmed"
  | "needs_merge"
  | "hidden"
  | "important"
  | "dismissed";

export interface WorkGraphEvidenceRef {
  id: string;
  source_client: WorkGraphSourceClient;
  source_id: string;
  label: string;
  summary: string;
  occurred_at?: string;
  confidence: number;
  redaction_state: WorkGraphRedactionState;
  metadata: Record<string, unknown>;
}

export interface WorkGraphAttributionSourceEvent {
  source_client: WorkGraphSourceClient;
  source_id: string;
  source_label: string;
  event_type:
    | "session_excerpt"
    | "client_extraction"
    | "runtime_hook"
    | "tool_signal"
    | "source_coverage"
    | "mcp_call"
    | "repo_artifact"
    | "slack_message"
    | "github_event"
    | "linear_event"
    | "gmail_message"
    | "calendar_event"
    | "doc_signal"
    | "manual_review";
  occurred_at?: string;
  evidence_ref: string;
  actor_ref?: string;
  artifact_refs?: string[];
  confidence: number;
  metadata: Record<string, unknown>;
}

export interface WorkGraphAttributionNode {
  id: string;
  kind: WorkGraphAttributionKind;
  label: string;
  summary: string;
  source_client?: WorkGraphSourceClient;
  evidence_refs: string[];
  linked_node_ids: string[];
  confidence: number;
  weight: number;
  review_state: WorkGraphReviewState;
  dedupe_key?: string;
  metadata: Record<string, unknown>;
}

export interface WorkGraphAttributionSpine {
  source_events: WorkGraphAttributionSourceEvent[];
  actions: WorkGraphAttributionNode[];
  decisions: WorkGraphAttributionNode[];
  artifacts: WorkGraphAttributionNode[];
  people: WorkGraphAttributionNode[];
  agents: WorkGraphAttributionNode[];
  tools: WorkGraphAttributionNode[];
  businesses: WorkGraphAttributionNode[];
  product_surfaces: WorkGraphAttributionNode[];
  goals: WorkGraphAttributionNode[];
  sources: WorkGraphAttributionNode[];
  initiative_candidates: WorkGraphAttributionNode[];
  evidence_refs: WorkGraphEvidenceRef[];
  confidence: number;
  dedupe_keys: string[];
  privacy: {
    redaction_state: "public_summary";
    raw_transcripts_included: false;
    public_summary_only: true;
  };
  review: {
    pending_count: number;
    correction_affordances: Array<"confirm" | "merge" | "hide" | "mark_important" | "launch_or_dismiss">;
  };
}

export interface WorkGraphFinding {
  type: WorkGraphFindingType;
  title: string;
  summary: string;
  source_client: WorkGraphSourceClient;
  source_id: string;
  evidence_ref: string;
  confidence: number;
  metadata: Record<string, unknown>;
}

export interface WorkGraphClientExtractionFinding {
  type: WorkGraphFindingType;
  title: string;
  summary: string;
  source_client?: WorkGraphSourceClient;
  source_id?: string;
  source_label?: string;
  evidence_ref?: string;
  confidence?: number;
  occurred_at?: string;
  actor_id?: string;
  redacted_verbatim?: string;
  privacy_state?: "public" | "redacted" | "private";
  metadata?: Record<string, unknown>;
}

export interface WorkGraphClientExtraction {
  schema_version?: string;
  extraction_id?: string;
  source_client: WorkGraphSourceClient;
  source_label?: string;
  collection_methods?: string[];
  searched_sources?: string[];
  search_queries?: Array<{
    id: string;
    lens: string;
    query: string;
    result_count?: number;
  }>;
  extraction_quality?: {
    confidence?: number;
    searched_session_count?: number;
    skipped_session_count?: number;
    notes?: string[];
  };
  findings: WorkGraphClientExtractionFinding[];
}

export interface WorkGraphClientExtractionSummary {
  extraction_id: string;
  source_client: WorkGraphSourceClient;
  source_label: string;
  collection_methods: string[];
  searched_source_count: number;
  searched_session_count: number;
  skipped_session_count: number;
  query_count: number;
  finding_count: number;
  confidence: number;
  notes: string[];
}

export interface WorkGraphAuditMethod {
  mode: "ai_client_session_search";
  searched_session_files: number;
  skipped_session_files: number;
  searched_message_count: number;
  retained_evidence_lines: number;
  searched_source_groups: number;
  extraction_lenses: string[];
  client_native_packs: Array<{
    source_client: WorkGraphSourceClient;
    source_label: string;
    searched_session_count: number;
    finding_count: number;
    confidence: number;
  }>;
  privacy_contract: string[];
  notes: string[];
}

export interface WorkGraphDomainSignal {
  id: string;
  label: string;
  summary: string;
  finding_count: number;
  source_clients: WorkGraphSourceClient[];
  evidence_refs: string[];
  confidence: number;
}

export interface WorkGraphSkillToolSignal {
  id: string;
  label: string;
  kind: "agent" | "skill" | "mcp_tool" | "client_tool" | "source";
  mention_count: number;
  source_clients: WorkGraphSourceClient[];
  evidence_refs: string[];
  confidence: number;
}

export interface WorkGraphExtractionSchema {
  schema_version: typeof WORK_GRAPH_EXTRACTION_SCHEMA_VERSION;
  mode: "ai_client_session_search";
  objective: string;
  privacy_contract: string[];
  source_search_strategy: Array<{
    id: string;
    lens: string;
    query: string;
    return_when: string;
  }>;
  required_output: Record<string, unknown>;
  quality_bar: string[];
  reject_as_noise: string[];
}

export interface WorkGraphExtractionProtocol {
  schema_version: typeof WORK_GRAPH_EXTRACTION_SCHEMA_VERSION;
  mode: "ai_client_session_search";
  prompt: string;
  schema: WorkGraphExtractionSchema;
}

export interface WorkGraphOpportunityScore {
  overall: number;
  value_potential: number;
  evidence_quality: number;
  urgency: number;
  owner_clarity: number;
  automation_potential: number;
  orgx_fit: number;
}

export interface WorkGraphExecutionQualityScore {
  overall: number;
  evidence_coverage: number;
  source_attribution: number;
  trail_depth: number;
  insight_depth: number;
  actionability: number;
  impact_confidence: number;
  notes: string[];
}

export interface WorkGraphImpactProjection {
  time_saved_hours_per_week: number;
  acceleration_percent: number;
  estimated_monthly_value_usd: number;
  confidence: number;
  basis: string[];
  assumptions: string[];
}

export interface WorkGraphKickoffCandidate {
  title: string;
  summary: string;
  reason: string;
  finding_refs: string[];
  priority: "p0" | "p1" | "p2";
}

export type WorkGraphEntityType =
  | "idea"
  | "initiative"
  | "workstream"
  | "milestone"
  | "task"
  | "decision"
  | "artifact"
  | "person"
  | "business"
  | "blocker"
  | "agent"
  | "tool"
  | "source"
  | "surface"
  | "outcome";

export type WorkGraphTrailKind =
  | "idea_trail"
  | "initiative_trail"
  | "decision_trail"
  | "artifact_trail"
  | "person_trail"
  | "business_trail"
  | "blocker_trail"
  | "agent_trail"
  | "tool_trail"
  | "source_trail"
  | "outcome_trail";

export type WorkGraphTrailState =
  | "observed"
  | "inferred"
  | "claimed"
  | "verified"
  | "missing_evidence"
  | "blocked"
  | "stale"
  | "decaying"
  | "resolved"
  | "disputed"
  | "contradicted";

export type WorkGraphTrailValence =
  | "healthy"
  | "useful_recurrence"
  | "wasteful_recurrence"
  | "risk"
  | "leak"
  | "opportunity"
  | "zombie"
  | "escalating";

export interface WorkGraphTrailEvent {
  id: string;
  trail_id: string;
  event_type:
    | "signal_detected"
    | "idea_created"
    | "initiative_created"
    | "decision_inferred"
    | "decision_promoted"
    | "artifact_created"
    | "artifact_verified"
    | "owner_assigned"
    | "blocker_detected"
    | "source_connected"
    | "outcome_recorded"
    | "recommendation_generated";
  entity_id: string;
  entity_type: WorkGraphEntityType;
  timestamp: string;
  actor_id?: string;
  source_id: string;
  source_type: WorkGraphSourceClient;
  redacted_verbatim?: string;
  confidence: number;
  evidence_refs: string[];
  privacy_state: "public" | "redacted" | "private";
}

export interface WorkGraphTrailEdge {
  id: string;
  from_event_id: string;
  to_event_id: string;
  relation:
    | "caused"
    | "informed"
    | "blocked"
    | "resolved"
    | "created"
    | "verified"
    | "assigned"
    | "depends_on"
    | "duplicates"
    | "recurred_as"
    | "converted_to"
    | "contradicted";
  state: "captured" | "inferred" | "missing";
  confidence: number;
  evidence_refs: string[];
}

export interface WorkGraphTrailRecommendation {
  id: string;
  title: string;
  summary: string;
  action_type: "launch_initiative" | "promote_decision" | "attach_artifact" | "assign_owner" | "connect_source" | "verify_outcome";
  trail_ids: string[];
  evidence_refs: string[];
  priority: "p0" | "p1" | "p2";
  expected_lift: string;
  confidence: number;
}

export interface WorkGraphTrail {
  id: string;
  kind: WorkGraphTrailKind;
  title: string;
  summary: string;
  subject_entity_id: string;
  subject_entity_type: WorkGraphEntityType;
  state: WorkGraphTrailState;
  valence: WorkGraphTrailValence;
  confidence: number;
  recurrence_score: number;
  impact_score: number;
  privacy_state: "public" | "redacted" | "private";
  events: WorkGraphTrailEvent[];
  edges: WorkGraphTrailEdge[];
  evidence_refs: string[];
  blocker_ids: string[];
  recommendation_ids: string[];
  created_at: string;
  updated_at: string;
  shape:
    | "single_signal"
    | "dense_recent_cluster"
    | "chronic_recurrence"
    | "zombie_revival"
    | "accelerating_issue"
    | "healthy_execution"
    | "decision_heavy_artifact_light"
    | "artifact_heavy_decision_light";
}

export interface WorkGraphRecurringPattern {
  id: string;
  title: string;
  description: string;
  pattern_type:
    | "trapped_decision"
    | "orphaned_artifact"
    | "missing_owner"
    | "repeated_work"
    | "missing_source"
    | "tooling_mismatch"
    | "unverified_outcome"
    | "handoff_friction"
    | "business_signal_unclaimed";
  affected_trail_ids: string[];
  affected_entity_ids: string[];
  recurrence_count: number;
  severity: "low" | "medium" | "high" | "critical";
  confidence: number;
  valence: WorkGraphTrailValence;
  root_cause_hypothesis: string;
  recommended_runtime_action_id?: string;
}

export interface WorkGraphMirrorClaim {
  id: string;
  text: string;
  evidence_refs: string[];
  confidence: number;
}

export interface WorkGraphMirror {
  headline: string;
  body: string;
  lens: "all" | "ideas" | "decisions" | "artifacts" | "people" | "businesses" | "blockers" | "agents_tools" | "sources";
  claims: WorkGraphMirrorClaim[];
  primary_trail_id?: string;
  generated_at: string;
}

export interface WorkGraphPageQualityScore {
  overall: number;
  clarity: number;
  impact: number;
  resonance: number;
  trust: number;
  repair_readiness: number;
  notes: string[];
}

export interface WorkGraphAgenticQuotient {
  aq: number;
  stack_score: number;
  durability_score: number;
  agentic_gap: number;
  ceiling: number;
  source_bonus: number;
  context_leak_score: number;
  cohort_percentile?: number;
  archetype: {
    id:
      | "ai_native_operator"
      | "context_leaker"
      | "decision_ghost"
      | "proof_maximalist"
      | "careful_operator"
      | "prompt_tourist"
      | "mcp_necromancer"
      | "agent_wrangler";
    /**
     * Memetic subtype — the screenshot-worthy identity flavor.
     * Examples: "MCP Necromancer", "Context Leaker".
     */
    label: string;
    /**
     * Plain-English primary — what kind of operator this is in one line.
     * Read by strangers without context. Examples: "Tool-Rich, Memory-Poor",
     * "Connected & Durable", "Heavy on Receipts".
     */
    primary: string;
    roast: string;
    truth: string;
    repair: string;
  };
  /**
   * Two-axis tier classification. Stack Usage tier × Durable Work tier.
   * Each axis has 6 tiers; the pair is the OrgX equivalent of a Strava class
   * (e.g. "AI-Maxed × Context Leaking"). Designed for stranger legibility —
   * even readers who do not know what an archetype is can compare tiers.
   */
  tiers: {
    stack: { tier: 1 | 2 | 3 | 4 | 5 | 6; label: string };
    durable: { tier: 1 | 2 | 3 | 4 | 5 | 6; label: string };
  };
  repair_quests: Array<{
    id: string;
    title: string;
    expected_aq_lift: number;
    reason: string;
    source_gap?: string;
    action_type: "connect_source" | "enable_runtime_hook" | "promote_decisions" | "attach_proof" | "launch_repair";
  }>;
  notes: string[];
}

export interface WorkGraphTensionMetric {
  id: string;
  label: string;
  value: string;
  tone: "good" | "warning" | "danger" | "muted";
  trail_ids: string[];
  evidence_refs: string[];
  explanation: string;
}

export interface WorkGraphFingerprintBasis {
  schema_version: typeof WORK_GRAPH_SCHEMA_VERSION;
  fingerprint_version: typeof WORK_GRAPH_FINGERPRINT_VERSION;
  workspace_hash: string;
  source_clients: WorkGraphSourceClient[];
  connected_source_hashes: string[];
  missing_source_hashes: string[];
  finding_type_counts: Partial<Record<WorkGraphFindingType, number>>;
  pattern_hashes: string[];
  trail_shape_hashes: string[];
  recurring_pattern_hashes: string[];
  kickoff_hashes: string[];
  raw_transcripts_included: false;
}

export interface WorkGraphSignupHydration {
  strategy: "work_graph_fingerprint_claim";
  hydration_key: string;
  eligible: boolean;
  notes: string[];
}

export interface SessionReconciliationReport {
  schema_version: typeof WORK_GRAPH_SCHEMA_VERSION;
  report_id: string;
  idempotency_key: string;
  work_graph_fingerprint: string;
  fingerprint_version: typeof WORK_GRAPH_FINGERPRINT_VERSION;
  fingerprint_basis: WorkGraphFingerprintBasis;
  signup_hydration: WorkGraphSignupHydration;
  generated_at: string;
  source_client: "wizard";
  session_id: string;
  workspace: WorkGraphWorkspaceRef;
  extraction_protocol: WorkGraphExtractionProtocol;
  audit_method: WorkGraphAuditMethod;
  client_extractions: WorkGraphClientExtractionSummary[];
  domain_coverage: WorkGraphDomainSignal[];
  skill_tool_signals: WorkGraphSkillToolSignal[];
  source_coverage: WorkGraphSourceCoverage;
  final_state: WorkGraphFinalState;
  events: WorkGraphSourceEvent[];
  findings: WorkGraphFinding[];
  missed_orchestration_opportunities: WorkGraphFinding[];
  trails: WorkGraphTrail[];
  recurring_patterns: WorkGraphRecurringPattern[];
  recommendations: WorkGraphTrailRecommendation[];
  mirror: WorkGraphMirror;
  tension_metrics: WorkGraphTensionMetric[];
  attribution_spine: WorkGraphAttributionSpine;
  opportunity_score: WorkGraphOpportunityScore;
  execution_quality: WorkGraphExecutionQualityScore;
  page_quality: WorkGraphPageQualityScore;
  agentic_quotient: WorkGraphAgenticQuotient;
  impact_projection: WorkGraphImpactProjection;
  investigation: WorkGraphInvestigationV2;
  initiative_kickoffs: WorkGraphKickoffCandidate[];
  redaction_level: "summary_only";
  raw_transcripts_sent: false;
}

export interface BuildSessionReconciliationReportInput {
  clientExtractions?: readonly WorkGraphClientExtraction[];
  connectedSources?: readonly string[];
  generatedAt?: string;
  investigationRawEvents?: readonly RawEvent[];
  imports: readonly FounderLoopImport[];
  missingSources?: readonly string[];
  sessionId?: string;
  workspace: WorkGraphWorkspaceRef;
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function hashJson(value: unknown): string {
  return createHash("sha256")
    .update(JSON.stringify(value))
    .digest("hex");
}

function normalizeFingerprintText(value: string): string {
  return value
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, "url")
    .replace(/[0-9a-f]{12,}/g, "hash")
    .replace(/\b[0-9a-f]{8}-[0-9a-f-]{27,}\b/g, "uuid")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 600);
}

function shortHash(value: unknown, length = 16): string {
  return hashJson(value).slice(0, length);
}

function sortedUnique<T extends string>(values: readonly T[]): T[] {
  return [...new Set(values)].sort();
}

function slugPart(value: string): string {
  const normalized = normalizeFingerprintText(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
  return normalized || "unknown";
}

function sourceClientForImport(source: FounderLoopImport): WorkGraphSourceClient {
  const raw = `${source.sourceId} ${source.sourceLabel}`.toLowerCase();
  if (raw.includes("codex")) return "codex";
  if (raw.includes("claude-code")) return "claude-code";
  if (raw.includes("claude")) return "claude";
  if (raw.includes("cursor")) return "cursor";
  if (raw.includes("opencode")) return "opencode";
  if (raw.includes("goose")) return "goose";
  if (raw.includes("opencode")) return "opencode";
  if (raw.includes("goose")) return "goose";
  if (raw.includes("openclaw")) return "openclaw";
  if (raw.includes("slack")) return "slack";
  if (raw.includes("github")) return "github";
  if (raw.includes("linear")) return "linear";
  if (raw.includes("gmail") || raw.includes("email")) return "gmail";
  if (raw.includes("calendar") || raw.includes("meeting")) return "calendar";
  if (raw.includes("notion")) return "notion";
  if (raw.includes("doc")) return "docs";
  if (raw.includes("mcp")) return "mcp";
  if (raw.includes("api")) return "api";
  if (raw.includes("manual") || raw.includes("wizard-audit-input")) return "manual";
  return "unknown";
}

function normalizeSourceClient(value: unknown): WorkGraphSourceClient {
  if (typeof value !== "string") return "unknown";
  const normalized = value.trim().toLowerCase();
  if (
    normalized === "codex" ||
    normalized === "claude" ||
    normalized === "claude-code" ||
    normalized === "cursor" ||
    normalized === "opencode" ||
    normalized === "goose" ||
    normalized === "openclaw" ||
    normalized === "slack" ||
    normalized === "mcp" ||
    normalized === "orgx_runtime_hook" ||
    normalized === "github" ||
    normalized === "linear" ||
    normalized === "gmail" ||
    normalized === "calendar" ||
    normalized === "notion" ||
    normalized === "docs" ||
    normalized === "manual" ||
    normalized === "wizard" ||
    normalized === "api"
  ) {
    return normalized;
  }
  if (normalized.includes("claude")) return "claude";
  if (normalized.includes("codex")) return "codex";
  if (normalized.includes("cursor")) return "cursor";
  if (normalized.includes("opencode")) return "opencode";
  if (normalized.includes("goose")) return "goose";
  if (normalized.includes("slack")) return "slack";
  if (normalized.includes("github")) return "github";
  if (normalized.includes("linear")) return "linear";
  if (normalized.includes("gmail") || normalized.includes("email")) return "gmail";
  if (normalized.includes("calendar") || normalized.includes("meeting")) return "calendar";
  if (normalized.includes("notion")) return "notion";
  if (normalized.includes("doc")) return "docs";
  if (normalized.includes("mcp")) return "mcp";
  if (normalized.includes("runtime") || normalized.includes("hook")) return "orgx_runtime_hook";
  return "unknown";
}

function sourceClientFromText(value: string): WorkGraphSourceClient {
  const normalized = value.toLowerCase();
  if (/\bclaude(?:[- ]code)?\b|\.claude\/projects|claude:/.test(normalized)) return "claude";
  if (/\bcodex\b|\.codex\/sessions|rollout-/.test(normalized)) return "codex";
  if (/\bcursor\b/.test(normalized)) return "cursor";
  if (/\bopencode\b/.test(normalized)) return "opencode";
  if (/\bgoose\b/.test(normalized)) return "goose";
  if (/\bopenclaw\b/.test(normalized)) return "openclaw";
  if (/\bslack\b/.test(normalized)) return "slack";
  if (/\bgithub\b|\bgit:|pull request|commit\b/.test(normalized)) return "github";
  if (/\blinear\b/.test(normalized)) return "linear";
  if (/\bgmail\b|\bemail\b/.test(normalized)) return "gmail";
  if (/\bcalendar\b|\bmeeting\b/.test(normalized)) return "calendar";
  if (/\bnotion\b/.test(normalized)) return "notion";
  if (/\bdocs?\b|google drive/.test(normalized)) return "docs";
  if (/\bruntime\b|\bhook\b|\boutbox\b/.test(normalized)) return "orgx_runtime_hook";
  if (/\bmcp\b|mcp__|orgx_emit|orgx_write|ship_batch/.test(normalized)) return "mcp";
  if (/\borgx api\b|\bapi\b/.test(normalized)) return "api";
  return "unknown";
}

function sourceClientForExtractionFinding(
  extraction: WorkGraphClientExtraction,
  finding: WorkGraphClientExtractionFinding,
): WorkGraphSourceClient {
  const explicit = normalizeSourceClient(finding.source_client);
  if (explicit !== "unknown") return explicit;
  const inferred = sourceClientFromText([
    finding.source_id ?? "",
    finding.source_label ?? "",
    finding.evidence_ref ?? "",
    finding.summary ?? "",
  ].join("\n"));
  if (inferred !== "unknown") return inferred;
  return normalizeSourceClient(extraction.source_client);
}

function arrayOfStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function normalizeFindingType(value: unknown): WorkGraphFindingType | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return WORK_GRAPH_FINDING_TYPES.includes(normalized as WorkGraphFindingType)
    ? normalized as WorkGraphFindingType
    : null;
}

function normalizeConfidence(value: unknown, fallback = 0.72): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.max(0.1, Math.min(0.99, Number(value.toFixed(2))));
}

function titleFromText(text: string, fallback: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("mcp__orgx__orgx_search") && lower.includes("zod")) {
    return "OrgX MCP orgx_search is failing schema validation";
  }
  if (lower.includes("mcporgxlistentities") || lower.includes("listentities")) {
    return "OrgX entity lookup needs human-readable evidence mapping";
  }
  if (lower.includes("orgx_write") && lower.includes("auto_continue") && lower.includes("dispatch")) {
    return "orgx_write creates ready streams without dispatching agent runs";
  }
  if (lower.includes("operation qa loop") && lower.includes("0/") && lower.includes("entities")) {
    return "OrgX scaffold fails to create Operation QA Loop entities";
  }
  if (lower.includes("production-only redirect") || lower.includes("livedemopageclient")) {
    return "Production route behavior changed without durable approval";
  }

  const normalized = text
    .replace(/[`*_>#]/g, "")
    .replace(/^\s*[-*\d.)]+\s*/, "")
    .replace(/^\s*(decision|artifact|commitment|next action|follow[- ]?up|outcome|roi|economics|open loop|gap|blocker|risk|goal|summary)\s*:\s*/i, "")
    .replace(/^\s*(fix|fixing|debug|investigate|continue|todo)\s+(the\s+)?(bug|issue|problem)\s*[:.-]?\s*/i, "")
    .replace(/^\s*one\s+bug\s+surfaced\s*:\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();
  const firstSentence = normalized.split(/[.!?]\s/)[0]?.trim() || normalized;
  const firstClause = firstSentence.split(/\s[-:;]\s/)[0]?.trim() || firstSentence;
  return (firstClause || fallback).slice(0, 110);
}

function normalizeClientExtractionId(extraction: WorkGraphClientExtraction, index: number): string {
  return extraction.extraction_id?.trim()
    || `${extraction.source_client || "unknown"}:client-extraction:${index + 1}`;
}

export function buildWorkGraphExtractionProtocol(): WorkGraphExtractionProtocol {
  const schema: WorkGraphExtractionSchema = {
    schema_version: WORK_GRAPH_EXTRACTION_SCHEMA_VERSION,
    mode: "ai_client_session_search",
    objective:
      "Search the AI client's own sessions, logs, tool calls, and local transcript index to extract durable work-graph evidence for OrgX.",
    privacy_contract: [
      "Do not return raw transcripts.",
      "Return short redacted verbatim snippets only when they materially support a finding.",
      "Prefer evidence refs, session ids, timestamps, source ids, and hashes over full content.",
      "Mark private customer/person data as private or redacted.",
      "Do not invent people, decisions, artifacts, or outcomes that are not supported by a source event.",
    ],
    source_search_strategy: [
      {
        id: "decisions",
        lens: "Decision evidence",
        query:
          "Find choices, tradeoffs, approvals, rejected options, contradictions, architecture calls, product calls, and decisions that were restated later.",
        return_when:
          "A choice shaped future work, blocked work, or should become durable organizational memory.",
      },
      {
        id: "artifacts",
        lens: "Artifact evidence",
        query:
          "Find created or modified artifacts: PRs, files, docs, designs, prompts, plans, reports, screenshots, deployed routes, tests, and verification receipts.",
        return_when:
          "The artifact has a source event, proof reference, downstream use, owner, or missing verification.",
      },
      {
        id: "blockers",
        lens: "Blocker evidence",
        query:
          "Find failed tool calls, timeouts, rejected validators, missing auth, missing source coverage, repeated unresolved questions, stalled owners, and external blockers.",
        return_when:
          "The blocker explains why work did not become durable, verified, assigned, or shipped.",
      },
      {
        id: "people_businesses",
        lens: "People and business context",
        query:
          "Find users, customers, buyers, stakeholders, reviewers, teams, businesses, accounts, deal context, support signals, and market signals connected to work or decisions.",
        return_when:
          "A person or business changes priority, ownership, revenue potential, customer pain, or follow-up urgency.",
      },
      {
        id: "coordination_sources",
        lens: "Coordination and calendar/email context",
        query:
          "Find Slack threads, emails, meetings, docs, handoffs, approvals, owner changes, follow-ups, and coordination gaps that explain how work moved or stalled outside the AI client.",
        return_when:
          "The coordination source proves ownership, urgency, approval, customer context, or a missing source needed for attribution.",
      },
      {
        id: "product_surfaces",
        lens: "Product surface coverage",
        query:
          "Find surfaces such as live rooms, command center, widgets, plugins, wizard flows, public pages, APIs, MCP tools, Slack/GitHub/Linear integrations, and signup/claim flows.",
        return_when:
          "A surface was changed, verified, blocked, requested, or connected to a goal or artifact.",
      },
      {
        id: "agents_tools_sources",
        lens: "Agent, skill, tool, and source usage",
        query:
          "Find agent runs, subagents, MCP calls, hook lifecycle events, tool availability, tool misses, source coverage, and runtime writeback behavior.",
        return_when:
          "The event proves whether OrgX was called, skipped, unavailable, or only mentioned in instructions.",
      },
      {
        id: "outcomes_roi",
        lens: "Outcome and ROI evidence",
        query:
          "Find shipped/completed work, test/browser/deploy verification, outcomes, time saved, cost, revenue, customer impact, attribution, and expected lift.",
        return_when:
          "The outcome can be tied to evidence and a prior decision, artifact, blocker, or source.",
      },
      {
        id: "recurrence",
        lens: "Recurring patterns",
        query:
          "Find repeated shapes across sessions: trapped decisions, orphaned artifacts, missing owner, repeated work, source gaps, tooling mismatch, unverified outcomes, handoff friction, and unclaimed business signals.",
        return_when:
          "The same pattern appears across multiple sessions, days, tools, actors, or product surfaces.",
      },
      {
        id: "domains",
        lens: "Domain coverage",
        query:
          "Find which domains the work spans: product/UX, agents/runtime, MCP/platform, wizard/CLI, plugin distribution, source integrations, quality verification, GTM, sales, operations, and business leverage.",
        return_when:
          "A domain has concrete work evidence, not just a generic mention.",
      },
      {
        id: "skills_tools",
        lens: "Commonly invoked skills, agents, and tools",
        query:
          "Find named skills, OrgX domain agents, MCP tools, client tools, commands, test tools, and source systems that shaped the work.",
        return_when:
          "A skill/tool/source was used, requested, blocked, missed, or repeatedly referenced as part of execution.",
      },
    ],
    required_output: {
      schema_version: WORK_GRAPH_EXTRACTION_SCHEMA_VERSION,
      extraction_id: "stable id for this extraction run",
      source_client: "codex | claude | claude-code | cursor | opencode | goose | openclaw | slack | mcp | github | linear | gmail | calendar | notion | docs | manual | api | unknown",
      source_label: "human-readable source label",
      searched_sources: ["session/log/source group names searched"],
      search_queries: [
        {
          id: "decisions",
          lens: "Decision evidence",
          query: "query actually used",
          result_count: 0,
        },
      ],
      extraction_quality: {
        confidence: 0.0,
        searched_session_count: 0,
        skipped_session_count: 0,
        notes: ["limits, permissions, or gaps"],
      },
      findings: [
        {
          type: "decision | artifact | blocker | person | business | product_surface | goal | outcome | action | initiative_candidate | missed_orchestration_opportunity",
          title: "short durable title, not a raw line",
          summary: "one sentence explaining why this matters",
          source_client: "optional override when a blended extractor found evidence from another client",
          source_id: "session/tool/source id",
          source_label: "source label",
          evidence_ref: "stable evidence pointer",
          confidence: 0.0,
          occurred_at: "ISO timestamp if known",
          actor_id: "redacted actor id if known",
          redacted_verbatim: "short supporting excerpt only",
          privacy_state: "public | redacted | private",
          metadata: {
            related_entity_names: [],
            related_source_ids: [],
            recurrence_count: 0,
            state: "observed | inferred | verified | blocked | missing_evidence",
          },
        },
      ],
    },
    quality_bar: [
      "Search broadly through message turns before summarizing; do not stop at the newest session if older sessions contain recurrence.",
      "Every finding must include a source id or evidence ref.",
      "A durable finding starts only from evidence-bearing work signals, not empty labels, code type signatures, tool catalogs, or guardrail text.",
      "Prefer fewer, higher-confidence findings over many shallow lines.",
      "Include negative evidence when OrgX/MCP was available but not called.",
      "Separate actual tool calls from textual mentions of tool names.",
      "Extract the range of work domains and common skills/tools so the reader can see what kinds of work the audit actually understood.",
      "Mark confidence below 0.6 when the finding is inferred from weak or single-source evidence.",
      "Do not emit raw JSON tool payloads; summarize the tool result into a blocker/artifact/outcome finding.",
    ],
    reject_as_noise: [
      "AGENTS.md or system guardrail instructions.",
      "Source-code paths and line-number references unless the code change itself is the artifact.",
      "Bare labels like Decision:, Receipt:, Impact:, or proof: {.",
      "Type signatures such as outcome: string; or decision: ReviewableDecision;",
      "Tool catalogs and lists of available MCP tools.",
      "Planning narration like let me check, now I will inspect, or I need to verify.",
      "Generic MCP/AI commentary without a concrete work event.",
    ],
  };

  const prompt = [
    "You are running the OrgX AI-client audit skill from your own session/search logs.",
    "Search across available local message turns, transcripts, tool-call logs, hook events, and source indexes using every lens in the schema.",
    "Return JSON only. Match the required_output shape exactly. Do not return raw transcripts.",
    "The output will create public-safe OrgX evidence paths, so every finding must be evidence-bearing and useful enough for a human to inspect.",
  ].join(" ");

  return {
    schema_version: WORK_GRAPH_EXTRACTION_SCHEMA_VERSION,
    mode: "ai_client_session_search",
    prompt,
    schema,
  };
}

function findingTypeForItem(item: FounderLoopItem): WorkGraphFindingType {
  switch (item.type) {
    case "decision":
      return "decision";
    case "artifact":
      return "artifact";
    case "open_loop":
      return "blocker";
    case "commitment":
    case "next_action":
    case "outcome":
      return "action";
    case "economics":
      return "business";
  }
}

function evidenceRefFor(sourceId: string, index: number): string {
  return `${sourceId}:derived:${index + 1}`;
}

function includesAny(text: string, patterns: readonly RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(text));
}

function buildCoverage(
  imports: readonly FounderLoopImport[],
  connectedSources: readonly string[],
  missingSources: readonly string[],
  clientExtractions: readonly WorkGraphClientExtraction[] = [],
): WorkGraphSourceCoverage {
  const findings = buildClientExtractionFindings(clientExtractions);
  const sourceClients = sortedUnique([
    ...imports.map(sourceClientForImport),
    ...findings.map((finding) => finding.source_client),
    ...clientExtractions.map((extraction) => normalizeSourceClient(extraction.source_client)),
  ].filter((source): source is WorkGraphSourceClient => source !== "unknown"));
  const extractionText = clientExtractions
    .flatMap((extraction) => [
      extraction.source_client,
      extraction.source_label ?? "",
      ...(extraction.searched_sources ?? []),
      ...(extraction.findings ?? []).flatMap((finding) => [
        finding.title,
        finding.summary,
        finding.redacted_verbatim ?? "",
      ]),
    ])
    .join("\n");
  const allText = `${imports.map((source) => source.text).join("\n")}\n${extractionText}`.toLowerCase();
  const orgxObserved = /\borgx\b|useorgx|mcp__orgx__|orgx_/i.test(allText);
  const mcpObserved = /\bmcp\b|mcp__|tool call|tools\/call|call_tool|orgx_emit_activity/i.test(allText);
  const orgxMcpCalled = /mcp__orgx__|orgx_emit_activity|orgx_apply_changeset|complete_with_proof|orgx_write/i.test(allText);
  const skillOnlySignal = orgxObserved && !orgxMcpCalled && /\bskill|instructions|agent|workflow\b/i.test(allText);
  const normalizedConnectedSources = sortedUnique(
    connectedSources.map((source) => {
      const client = sourceClientFromText(source);
      return client === "unknown" ? source : labelForSourceClient(client);
    }),
  );
  const inferredConnected = [
    ...normalizedConnectedSources,
    ...(sourceClients.includes("codex") ? ["Codex sessions"] : []),
    ...(sourceClients.includes("claude") || sourceClients.includes("claude-code") ? ["Claude Code sessions"] : []),
    ...(sourceClients.includes("cursor") ? ["Cursor workspace evidence"] : []),
    ...(sourceClients.includes("opencode") ? ["OpenCode sessions"] : []),
    ...(sourceClients.includes("goose") ? ["goose sessions"] : []),
    ...(sourceClients.includes("github") ? ["Git/GitHub proof"] : []),
    ...(sourceClients.includes("mcp") ? ["MCP tool telemetry"] : []),
    ...(sourceClients.includes("orgx_runtime_hook") ? ["OrgX runtime hook replay"] : []),
    ...(sourceClients.includes("slack") ? ["Slack coordination"] : []),
  ];
  const inferredMissing = [
    ...missingSources.map(normalizeMissingSourceLabel),
    ...(sourceClients.includes("slack") ? [] : ["Slack coordination"]),
    ...(sourceClients.includes("github") ? [] : ["GitHub PR/commit proof"]),
    ...(allText.includes("hook") || allText.includes("outbox") ? [] : ["Runtime hook outbox replay"]),
  ];
  const connected = sortedUnique(inferredConnected.filter(Boolean));
  const missing = sortedUnique(inferredMissing.filter((source) => !connected.includes(source)));
  const manifests = buildSourceCoverageManifests({
    clientExtractions,
    connectedSources: connected,
    findings,
    imports,
    missingSources: missing,
  });
  const partialCount = manifests.filter((manifest) => manifest.status === "partial").length;
  const coverageScore = clampScore(
    28 +
      Math.min(34, connected.length * 7) +
      Math.min(18, findings.length * 2) +
      (sourceClients.includes("codex") ? 6 : 0) +
      (sourceClients.includes("claude") || sourceClients.includes("claude-code") ? 6 : 0) +
      (sourceClients.includes("cursor") ? 4 : 0) +
      (sourceClients.includes("opencode") ? 4 : 0) +
      (sourceClients.includes("goose") ? 4 : 0) +
      (sourceClients.includes("github") ? 6 : 0) +
      (sourceClients.includes("slack") ? 5 : 0) +
      (sourceClients.includes("orgx_runtime_hook") || sourceClients.includes("mcp") ? 7 : 0) -
      partialCount * 3,
  );

  return {
    connected,
    missing,
    mcpObserved,
    orgxObserved,
    orgxMcpCalled,
    skillOnlySignal,
    coverage_score: coverageScore,
    manifests,
    notes: [
      "OrgX/MCP writeback is one coverage signal; it is not required for the audit to find useful work.",
      missing.length > 0
        ? `Additional sources can raise the ceiling: ${missing.join(", ")}.`
        : "Connected sources are sufficient for a first-pass operating profile, but still require user review.",
    ],
  };
}

function buildSourceCoverageManifests(input: {
  clientExtractions: readonly WorkGraphClientExtraction[];
  connectedSources: readonly string[];
  findings: readonly WorkGraphFinding[];
  imports: readonly FounderLoopImport[];
  missingSources: readonly string[];
}): WorkGraphSourceCoverageManifest[] {
  const manifests = new Map<string, WorkGraphSourceCoverageManifest>();
  const addManifest = (manifest: WorkGraphSourceCoverageManifest) => {
    const sourceLabel = canonicalSourceManifestLabel(manifest);
    const key = `${manifest.source_client}:${sourceLabel}`;
    const previous = manifests.get(key);
    if (!previous) {
      manifests.set(key, { ...manifest, source_label: sourceLabel });
      return;
    }
    manifests.set(key, {
      ...previous,
      source_label: sourceLabel,
      status: previous.status === "connected" || manifest.status === "connected"
        ? "connected"
        : previous.status === "partial" || manifest.status === "partial"
          ? "partial"
          : "missing",
      searched_sources: sortedUnique([...previous.searched_sources, ...manifest.searched_sources]),
      searched_session_count: previous.searched_session_count + manifest.searched_session_count,
      skipped_session_count: previous.skipped_session_count + manifest.skipped_session_count,
      query_count: previous.query_count + manifest.query_count,
      finding_count: previous.finding_count + manifest.finding_count,
      confidence: Math.max(previous.confidence, manifest.confidence),
      notes: sortedUnique([...previous.notes, ...manifest.notes]),
    });
  };

  for (const extraction of input.clientExtractions) {
    const extractionFindings = buildClientExtractionFindings([extraction]);
    const byClient = new Map<WorkGraphSourceClient, WorkGraphFinding[]>();
    for (const finding of extractionFindings) {
      byClient.set(finding.source_client, [...(byClient.get(finding.source_client) ?? []), finding]);
    }
    const extractionClient = normalizeSourceClient(extraction.source_client);
    if (!byClient.has(extractionClient)) {
      addManifest({
        source_client: extractionClient,
        source_label: extraction.source_label?.trim() || labelForSourceClient(extractionClient),
        status: "partial",
        searched_sources: extraction.searched_sources ?? [],
        searched_session_count: extraction.extraction_quality?.searched_session_count ?? 0,
        skipped_session_count: extraction.extraction_quality?.skipped_session_count ?? 0,
        query_count: extraction.search_queries?.length ?? 0,
        finding_count: 0,
        confidence: normalizeConfidence(extraction.extraction_quality?.confidence, 0.74),
        notes: extraction.extraction_quality?.notes ?? [],
      });
    }
    for (const [sourceClient, findings] of byClient) {
      const searchedSources = extraction.searched_sources ?? [];
      addManifest({
        source_client: sourceClient,
        source_label: labelForSourceClient(sourceClient),
        status: findings.length > 0 ? "connected" : "partial",
        searched_sources: searchedSources,
        searched_session_count: extraction.extraction_quality?.searched_session_count ?? 0,
        skipped_session_count: extraction.extraction_quality?.skipped_session_count ?? 0,
        query_count: extraction.search_queries?.length ?? 0,
        finding_count: findings.length,
        confidence: normalizeConfidence(extraction.extraction_quality?.confidence, 0.74),
        notes: extraction.extraction_quality?.notes ?? [],
      });
    }
  }

  for (const source of input.imports) {
    const sourceClient = sourceClientForImport(source);
    addManifest({
      source_client: sourceClient,
      source_label: source.sourceLabel,
      status: "connected",
      searched_sources: [source.sourceId],
      searched_session_count: 1,
      skipped_session_count: 0,
      query_count: 0,
      finding_count: input.findings.filter((finding) => finding.source_id === source.sourceId).length,
      confidence: 0.62,
      notes: ["Fallback line-level import; lower confidence than client-native extraction."],
    });
  }

  for (const source of input.missingSources) {
    addManifest({
      source_client: sourceClientFromText(source),
      source_label: source,
      status: "missing",
      searched_sources: [],
      searched_session_count: 0,
      skipped_session_count: 0,
      query_count: 0,
      finding_count: 0,
      confidence: 0,
      notes: ["Not connected or not searched in this audit pass."],
    });
  }

  return [...manifests.values()].sort((left, right) => {
    const rank = { connected: 0, partial: 1, missing: 2 };
    return rank[left.status] - rank[right.status] || right.finding_count - left.finding_count;
  });
}

function canonicalSourceManifestLabel(manifest: WorkGraphSourceCoverageManifest): string {
  if (
    manifest.source_client === "codex" ||
    manifest.source_client === "claude" ||
    manifest.source_client === "claude-code" ||
    manifest.source_client === "cursor" ||
    manifest.source_client === "opencode" ||
    manifest.source_client === "goose"
  ) {
    return labelForSourceClient(manifest.source_client);
  }
  if (manifest.source_client === "github" || manifest.source_client === "slack" || manifest.source_client === "mcp") {
    return labelForSourceClient(manifest.source_client);
  }
  return manifest.source_label;
}

function labelForSourceClient(sourceClient: WorkGraphSourceClient): string {
  switch (sourceClient) {
    case "codex":
      return "Codex sessions";
    case "claude":
    case "claude-code":
      return "Claude Code sessions";
    case "cursor":
      return "Cursor workspace evidence";
    case "opencode":
      return "OpenCode sessions";
    case "goose":
      return "goose sessions";
    case "mcp":
      return "MCP tool telemetry";
    case "orgx_runtime_hook":
      return "OrgX runtime hook replay";
    case "github":
      return "Git/GitHub proof";
    case "slack":
      return "Slack coordination";
    case "linear":
      return "Linear issues";
    case "gmail":
      return "Email coordination";
    case "calendar":
      return "Calendar/meeting context";
    case "wizard":
      return "Wizard audit layer";
    case "notion":
    case "docs":
      return "Docs";
    default:
      return sourceClient.replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  }
}

function normalizeMissingSourceLabel(source: string): string {
  if (/slack/i.test(source)) return "Slack coordination";
  if (/github|git\/github|commit|pr/i.test(source)) return "GitHub PR/commit proof";
  if (/runtime|hook|outbox/i.test(source)) return "Runtime hook outbox replay";
  if (/codex/i.test(source)) return "Codex session history";
  if (/claude/i.test(source)) return "Claude Code sessions";
  if (/cursor/i.test(source)) return "Cursor workspace history";
  if (/opencode/i.test(source)) return "OpenCode sessions";
  if (/goose/i.test(source)) return "goose sessions";
  return source.replace(/\s+(?:store|readable events|audit-relevant session lines)$/i, "").trim();
}

export function summarizeClientExtractions(
  clientExtractions: readonly WorkGraphClientExtraction[],
): WorkGraphClientExtractionSummary[] {
  return clientExtractions.map((extraction, index) => {
    const extractionId = normalizeClientExtractionId(extraction, index);
    return {
      extraction_id: extractionId,
      source_client: normalizeSourceClient(extraction.source_client),
      source_label: extraction.source_label?.trim() || `${extraction.source_client || "Unknown"} AI-client extraction`,
      collection_methods: [...new Set(extraction.collection_methods ?? [])].sort(),
      searched_source_count: extraction.searched_sources?.length ?? 0,
      searched_session_count: extraction.extraction_quality?.searched_session_count ?? 0,
      skipped_session_count: extraction.extraction_quality?.skipped_session_count ?? 0,
      query_count: extraction.search_queries?.length ?? 0,
      finding_count: extraction.findings.length,
      confidence: normalizeConfidence(extraction.extraction_quality?.confidence, 0.74),
      notes: extraction.extraction_quality?.notes ?? [],
    };
  });
}

export function buildClientExtractionEvents(
  clientExtractions: readonly WorkGraphClientExtraction[],
): WorkGraphSourceEvent[] {
  return summarizeClientExtractions(clientExtractions).map((summary) => ({
    schema_version: WORK_GRAPH_SCHEMA_VERSION,
    source_client: summary.source_client,
    source_id: summary.extraction_id,
    source_label: summary.source_label,
    event_type: "client_extraction",
    text: [
      `${summary.finding_count} structured findings extracted by ${summary.source_client}.`,
      `${summary.query_count} search queries across ${summary.searched_source_count} searched source groups.`,
      `Extraction confidence: ${summary.confidence}.`,
    ].join(" "),
    evidence_ref: `${summary.extraction_id}:summary`,
    metadata: {
      collection_methods: summary.collection_methods,
      finding_count: summary.finding_count,
      query_count: summary.query_count,
      searched_source_count: summary.searched_source_count,
      searched_session_count: summary.searched_session_count,
      skipped_session_count: summary.skipped_session_count,
      raw_transcript_sent: false,
    },
  }));
}

function numberFromImportMetadata(source: FounderLoopImport, key: string): number {
  const value = source.metadata?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function buildAuditMethod(input: {
  clientExtractions: readonly WorkGraphClientExtraction[];
  extractionProtocol: WorkGraphExtractionProtocol;
  imports: readonly FounderLoopImport[];
}): WorkGraphAuditMethod {
  const extractionSummaries = summarizeClientExtractions(input.clientExtractions);
  const nativePacks = extractionSummaries
    .filter((summary) =>
      summary.source_client === "codex" ||
      summary.source_client === "claude" ||
      summary.source_client === "claude-code" ||
      summary.source_client === "cursor" ||
      summary.source_client === "opencode" ||
      summary.source_client === "goose"
    )
    .map((summary) => ({
      source_client: summary.source_client,
      source_label: summary.source_label,
      searched_session_count: summary.searched_session_count,
      finding_count: summary.finding_count,
      confidence: summary.confidence,
    }));
  const searchedMessages = input.imports.reduce(
    (total, source) => total + numberFromImportMetadata(source, "message_count"),
    0,
  );
  const retainedLines = input.imports.reduce((total, source) => {
    const explicit = numberFromImportMetadata(source, "retained_line_count");
    return total + (explicit || source.text.split(/\r?\n/).filter(Boolean).length);
  }, 0);
  const extractionRetainedLines = extractionSummaries.reduce(
    (total, summary) => total + summary.finding_count,
    0,
  );
  const skippedFiles = input.imports.reduce(
    (total, source) => total + numberFromImportMetadata(source, "skipped_file_count"),
    0,
  );
  const extractionSearchedSessions = extractionSummaries.reduce(
    (total, summary) => total + summary.searched_session_count,
    0,
  );
  const extractionSkippedSessions = extractionSummaries.reduce(
    (total, summary) => total + summary.skipped_session_count,
    0,
  );
  const sourceGroups = sortedUnique([
    ...input.imports.map((source) => source.sourceLabel),
    ...input.clientExtractions.flatMap((extraction) => extraction.searched_sources ?? []),
  ]);

  return {
    mode: "ai_client_session_search",
    searched_session_files: input.imports.length + extractionSearchedSessions,
    skipped_session_files: skippedFiles + extractionSkippedSessions,
    searched_message_count: searchedMessages,
    retained_evidence_lines: retainedLines + extractionRetainedLines,
    searched_source_groups: sourceGroups.length,
    extraction_lenses: input.extractionProtocol.schema.source_search_strategy.map((lens) => lens.lens),
    client_native_packs: nativePacks,
    privacy_contract: input.extractionProtocol.schema.privacy_contract,
    notes: [
      "The wizard reads local Codex/Claude message turns, filters out guardrails/code noise, then runs native extraction packs over the retained evidence lines.",
      "Counts describe local files/message turns inspected before redaction; raw transcripts are not stored in the report.",
      nativePacks.length > 0
        ? "Codex/Claude native packs classified session evidence into decisions, artifacts, blockers, domains, skills/tools, and missed writeback signals."
        : "No native AI-client pack produced findings; this report is fallback-import only.",
    ],
  };
}

export function buildClientExtractionFindings(
  clientExtractions: readonly WorkGraphClientExtraction[],
): WorkGraphFinding[] {
  const findings: WorkGraphFinding[] = [];

  clientExtractions.forEach((extraction, extractionIndex) => {
    const extractionId = normalizeClientExtractionId(extraction, extractionIndex);
    const extractionSourceClient = normalizeSourceClient(extraction.source_client);
    const sourceLabel = extraction.source_label?.trim() || `${extractionSourceClient} AI-client extraction`;
    extraction.findings.forEach((finding, findingIndex) => {
      const type = normalizeFindingType(finding.type);
      const title = finding.title?.trim();
      const summary = finding.summary?.trim();
      if (!type || !title || !summary) return;
      const sourceClient = sourceClientForExtractionFinding(extraction, finding);

      findings.push({
        type,
        title: title.slice(0, 180),
        summary,
        source_client: sourceClient,
        source_id: finding.source_id?.trim() || extractionId,
        evidence_ref: finding.evidence_ref?.trim() || `${extractionId}:F${findingIndex + 1}`,
        confidence: normalizeConfidence(finding.confidence, normalizeConfidence(extraction.extraction_quality?.confidence, 0.76)),
        metadata: {
          source_label: finding.source_label?.trim() || sourceLabel,
          extraction_id: extractionId,
          schema_version: extraction.schema_version ?? null,
          occurred_at: finding.occurred_at ?? null,
          actor_id: finding.actor_id ?? null,
          redacted_verbatim: finding.redacted_verbatim ?? null,
          privacy_state: finding.privacy_state ?? "redacted",
          ...(finding.metadata ?? {}),
        },
      });
    });
  });

  return findings;
}

function nativeExtractionFindingType(line: string): WorkGraphFindingType | null {
  const lower = line.toLowerCase();
  if (/^\s*(?:decision|decided|choice|approved|rejected)\s*:/i.test(line)) return "decision";
  if (/^\s*(?:artifact|receipt|proof)\s*:/i.test(line)) return "artifact";
  if (/^\s*(?:outcome|result|impact)\s*:/i.test(line)) return "outcome";
  if (/^\s*(?:roi|economics)\s*:/i.test(line)) return "business";
  if (/^\s*(?:next action|follow[- ]?up|commitment)\s*:/i.test(line)) return "action";
  if (/^\s*(?:blocker|risk|gap|open loop|rollback)\s*:/i.test(line)) return lower.includes("source") ? "missed_orchestration_opportunity" : "blocker";
  if (/\b(?:failed|fails|error|invalid|unavailable|timeout|timed out|not called|not dispatched|missing|broken)\b/i.test(line)) {
    if (/\b(?:tool|mcp|orgx|writeback|source|hook|outbox)\b/i.test(line)) return "missed_orchestration_opportunity";
    return "blocker";
  }
  if (/\b(?:created|generated|implemented|shipped|verified|passed)\b/i.test(line)) return "artifact";
  if (/\b(?:owner|dri|assigned)\b/i.test(line)) return "person";
  if (/\b(?:initiative|workstream|milestone|launch|goal)\b/i.test(line)) return "goal";
  return null;
}

function nativeEpisodeType(line: string): string {
  if (/\b(?:plan|decide|decision|choice|tradeoff|approve|reject)\b/i.test(line)) return "planning";
  if (/\b(?:implemented|created|generated|edited|file|diff|commit|artifact)\b/i.test(line)) return "implementation";
  if (/\b(?:failed|fails|error|timeout|invalid|debug|retry|blocked|blocker)\b/i.test(line)) return "debugging";
  if (/\b(?:verified|passed|test|browser|qa|proof|outcome)\b/i.test(line)) return "verification";
  return "handoff";
}

function nativeFindingConfidence(type: WorkGraphFindingType, line: string): number {
  let confidence = type === "decision" || type === "artifact" || type === "outcome" ? 0.78 : 0.72;
  if (/^\s*(?:decision|artifact|blocker|outcome|proof|result|next action|open loop|risk|gap)\s*:/i.test(line)) confidence += 0.08;
  if (/\b(?:mcp__orgx__|orgx_write|orgx_emit_activity|complete_with_proof|github|codex|claude)\b/i.test(line)) confidence += 0.05;
  if (line.length < 32) confidence -= 0.1;
  return normalizeConfidence(confidence, 0.72);
}

function nativeRelatedEntityNames(line: string): string[] {
  const candidates = [
    ...Array.from(line.matchAll(/`([^`]{3,80})`/g)).map((match) => match[1] ?? ""),
    ...Array.from(line.matchAll(/\b(?:OrgX|MCP|Codex|Claude|Slack|GitHub|Cursor|ChatGPT|Work Graph|Profile|Scaffold Initiative|Operation QA Loop|Runtime Hook|Eject Bay|Trail Inspector)\b/gi)).map((match) => match[0] ?? ""),
  ];
  return sortedUnique(
    candidates
      .map((candidate) => candidate.trim())
      .filter((candidate) => candidate.length >= 3)
      .slice(0, 8)
  );
}

function supportsNativeExtraction(sourceClient: WorkGraphSourceClient) {
  return sourceClient === "codex" || sourceClient === "claude" || sourceClient === "claude-code";
}

export function buildNativeAiClientExtractions(
  imports: readonly FounderLoopImport[],
  generatedAt = new Date().toISOString(),
): WorkGraphClientExtraction[] {
  const grouped = new Map<WorkGraphSourceClient, {
    findings: WorkGraphClientExtractionFinding[];
    searchedSources: string[];
    searchedSessionCount: number;
    skippedSessionCount: number;
  }>();

  for (const source of imports) {
    const sourceClient = sourceClientForImport(source);
    if (!supportsNativeExtraction(sourceClient)) continue;

    const bucket = grouped.get(sourceClient) ?? {
      findings: [],
      searchedSources: [],
      searchedSessionCount: 0,
      skippedSessionCount: 0,
    };
    bucket.searchedSources.push(source.sourceId);
    bucket.searchedSessionCount += 1;

    source.text.split(/\r?\n/).forEach((line, lineIndex) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const type = nativeExtractionFindingType(trimmed);
      if (!type) {
        bucket.skippedSessionCount += 1;
        return;
      }
      bucket.findings.push({
        type,
        title: titleFromText(trimmed, type.replace(/_/g, " ")),
        summary: trimmed,
        source_client: sourceClient,
        source_id: source.sourceId,
        source_label: source.sourceLabel,
        evidence_ref: `${source.sourceId}:native:${lineIndex + 1}`,
        confidence: nativeFindingConfidence(type, trimmed),
        occurred_at: generatedAt,
        redacted_verbatim: trimmed.slice(0, 420),
        privacy_state: "redacted",
        metadata: {
          episode_type: nativeEpisodeType(trimmed),
          related_entity_names: nativeRelatedEntityNames(trimmed),
          related_source_ids: [source.sourceId],
          state: type === "blocker" || type === "missed_orchestration_opportunity"
            ? "blocked"
            : type === "outcome" || type === "artifact"
              ? "verified"
              : "observed",
        },
      });
    });

    grouped.set(sourceClient, bucket);
  }

  return [...grouped.entries()]
    .filter(([, bucket]) => bucket.findings.length > 0)
    .map(([sourceClient, bucket]) => ({
      schema_version: WORK_GRAPH_EXTRACTION_SCHEMA_VERSION,
      extraction_id: `${sourceClient}:native-extraction:${shortHash({
        sources: bucket.searchedSources,
        findings: bucket.findings.map((finding) => finding.evidence_ref),
      }, 12)}`,
      source_client: sourceClient,
      source_label: `${labelForSourceClient(sourceClient)} native extraction`,
      searched_sources: sortedUnique(bucket.searchedSources),
      search_queries: buildWorkGraphExtractionProtocol().schema.source_search_strategy.map((lens) => ({
        id: lens.id,
        lens: lens.lens,
        query: lens.query,
        result_count: bucket.findings.filter((finding) => {
          const text = `${finding.title ?? ""}\n${finding.summary ?? ""}`;
          if (lens.id === "decisions") return finding.type === "decision";
          if (lens.id === "artifacts") return finding.type === "artifact";
          if (lens.id === "blockers") return finding.type === "blocker" || finding.type === "missed_orchestration_opportunity";
          if (lens.id === "people_businesses") return finding.type === "person" || finding.type === "business";
          if (lens.id === "product_surfaces") return finding.type === "product_surface" || /\b(surface|profile|wizard|plugin|mcp)\b/i.test(text);
          if (lens.id === "agents_tools_sources") return /\b(agent|skill|tool|mcp|codex|claude|github|slack)\b/i.test(text);
          if (lens.id === "outcomes_roi") return finding.type === "outcome" || finding.type === "business";
          if (lens.id === "domains") return nativeRelatedEntityNames(text).length > 0;
          if (lens.id === "skills_tools") return /\b(orgx-design|agent|skill|tool|mcp|codex|claude|github|slack)\b/i.test(text);
          return bucket.findings.length > 1 ? 1 : 0;
        }).length,
      })),
      extraction_quality: {
        confidence: normalizeConfidence(
          bucket.findings.reduce((total, finding) => total + (finding.confidence ?? 0.72), 0) / bucket.findings.length,
          0.74,
        ),
        searched_session_count: bucket.searchedSessionCount,
        skipped_session_count: bucket.skippedSessionCount,
        notes: [
          "Auto-generated by orgx-wizard from local AI-client session imports.",
          "Raw transcripts are excluded; only redacted evidence snippets and source refs are retained.",
        ],
      },
      findings: bucket.findings,
    }));
}

function buildDerivedFindings(imports: readonly FounderLoopImport[]): WorkGraphFinding[] {
  const findings: WorkGraphFinding[] = [];
  let derivedIndex = 0;

  for (const source of imports) {
    const sourceClient = sourceClientForImport(source);
    const lines = source.text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

    for (const line of lines) {
      const lower = line.toLowerCase();

      if (includesAny(lower, [/\b(owner|dri|founder|teammate|customer|user|stakeholder|buyer|reviewer)\b/])) {
        findings.push({
          type: "person",
          title: titleFromText(line, "Person or stakeholder signal"),
          summary: line,
          source_client: sourceClient,
          source_id: source.sourceId,
          evidence_ref: evidenceRefFor(source.sourceId, derivedIndex++),
          confidence: 0.62,
          metadata: { source_label: source.sourceLabel },
        });
      }

      if (includesAny(lower, [/\b(product surface|surface|live room|command center|dashboard|widget|plugin|wizard|audit|onboarding|mcp|slack|github|linear)\b/])) {
        findings.push({
          type: "product_surface",
          title: titleFromText(line, "Product surface signal"),
          summary: line,
          source_client: sourceClient,
          source_id: source.sourceId,
          evidence_ref: evidenceRefFor(source.sourceId, derivedIndex++),
          confidence: 0.66,
          metadata: { source_label: source.sourceLabel },
        });
      }

      if (includesAny(lower, [/\b(goal|objective|initiative|workstream|milestone|roadmap|launch)\b/])) {
        findings.push({
          type: "goal",
          title: titleFromText(line, "Goal signal"),
          summary: line,
          source_client: sourceClient,
          source_id: source.sourceId,
          evidence_ref: evidenceRefFor(source.sourceId, derivedIndex++),
          confidence: 0.64,
          metadata: { source_label: source.sourceLabel },
        });
      }
    }
  }

  return findings;
}

const WORK_GRAPH_DOMAIN_RULES: Array<{
  id: string;
  label: string;
  summary: string;
  patterns: RegExp[];
}> = [
  {
    id: "domain:orgx-agents-runtime",
    label: "OrgX agents and runtime",
    summary: "Agent orchestration, MCP writeback, hooks, approvals, and runtime governance.",
    patterns: [/\bagent\b/i, /\bsubagent\b/i, /\bmcp\b/i, /\bhook\b/i, /\bwriteback\b/i, /\borgx_emit\b/i, /\bscaffold_initiative\b/i],
  },
  {
    id: "domain:wizard-cli",
    label: "Wizard and CLI",
    summary: "OrgX Wizard setup, audit/profile generation, local release, and onboarding commands.",
    patterns: [/\bwizard\b/i, /\bcli\b/i, /\bnpm\b/i, /\brelease\b/i, /\bpublish\b/i, /\bwork-graph\b/i, /\bsessions reconcile\b/i],
  },
  {
    id: "domain:public-profile-ux",
    label: "Public profile and UX",
    summary: "Shareable readout, claim flow, mobile/desktop layout, evidence inspection, and profile copy.",
    patterns: [/\bprofile\b/i, /\bpublic readout\b/i, /\bclaim\b/i, /\bmobile\b/i, /\bdesktop\b/i, /\bux\b/i, /\bmirror\b/i, /\btrail inspector\b/i],
  },
  {
    id: "domain:plugins-integrations",
    label: "Plugins and integrations",
    summary: "Codex, Claude Code, Cursor/OpenClaw, Slack, GitHub, Linear, and source coverage work.",
    patterns: [/\bplugin\b/i, /\bcodex\b/i, /\bclaude\b/i, /\bcursor\b/i, /\bopenclaw\b/i, /\bslack\b/i, /\bgithub\b/i, /\blinear\b/i],
  },
  {
    id: "domain:quality-verification",
    label: "Quality and verification",
    summary: "Tests, browser QA, type checks, release proof, and stale/fixed verification.",
    patterns: [/\btest\b/i, /\btypecheck\b/i, /\bqa\b/i, /\bverified\b/i, /\bproof\b/i, /\bplaywright\b/i, /\bbrowser\b/i, /\bci\b/i],
  },
  {
    id: "domain:gtm-business",
    label: "GTM and business leverage",
    summary: "ICP framing, pricing/value estimates, outreach, buyers, revenue, and market-facing proof.",
    patterns: [/\bicp\b/i, /\bbuyer\b/i, /\bpricing\b/i, /\brevenue\b/i, /\bsales\b/i, /\bmarketing\b/i, /\bgtm\b/i, /\boperator leverage\b/i],
  },
];

export function buildDomainCoverage(findings: readonly WorkGraphFinding[]): WorkGraphDomainSignal[] {
  const signals = WORK_GRAPH_DOMAIN_RULES.map((rule) => {
    const matched = findings.filter((finding) => {
      const text = `${finding.title}\n${finding.summary}\n${finding.metadata.redacted_verbatim ?? ""}`;
      return rule.patterns.some((pattern) => pattern.test(text));
    });
    if (matched.length === 0) return null;
    return {
      id: rule.id,
      label: rule.label,
      summary: rule.summary,
      finding_count: matched.length,
      source_clients: sortedUnique(matched.map((finding) => finding.source_client)),
      evidence_refs: sortedUnique(matched.map((finding) => finding.evidence_ref)).slice(0, 12),
      confidence: Number((
        matched.reduce((total, finding) => total + finding.confidence, 0) / matched.length
      ).toFixed(2)),
    } satisfies WorkGraphDomainSignal;
  }).filter((signal): signal is WorkGraphDomainSignal => Boolean(signal));

  return signals
    .sort((left, right) => right.finding_count - left.finding_count || right.confidence - left.confidence)
    .slice(0, 8);
}

const SKILL_TOOL_RULES: Array<{
  id: string;
  kind: WorkGraphSkillToolSignal["kind"];
  label: string;
  pattern: RegExp;
}> = [
  { id: "skill:orgx-design", kind: "skill", label: "orgx-design", pattern: /\borgx-design\b|\$orgx-design/i },
  { id: "skill:runtime-reporting", kind: "skill", label: "orgx-runtime-reporting", pattern: /\borgx-runtime-reporting\b|runtime reporting/i },
  { id: "skill:initiative-ops", kind: "skill", label: "orgx-initiative-ops", pattern: /\borgx-initiative-ops\b|initiative ops/i },
  { id: "agent:engineering", kind: "agent", label: "engineering-agent", pattern: /\bengineering-agent\b|engineering agent/i },
  { id: "agent:design", kind: "agent", label: "design-agent", pattern: /\bdesign-agent\b|design agent/i },
  { id: "agent:product", kind: "agent", label: "product-agent", pattern: /\bproduct-agent\b|product agent/i },
  { id: "agent:orchestrator", kind: "agent", label: "orchestrator-agent", pattern: /\borchestrator-agent\b|orchestrator agent/i },
  { id: "tool:orgx-list-entities", kind: "mcp_tool", label: "mcp__orgx__orgx_search", pattern: /\bmcp__orgx__orgx_search\b/i },
  { id: "tool:scaffold", kind: "mcp_tool", label: "orgx_write", pattern: /\bscaffold_initiative\b/i },
  { id: "tool:complete-proof", kind: "mcp_tool", label: "complete_with_proof", pattern: /\bcomplete_with_proof\b/i },
  { id: "tool:ship-batch", kind: "mcp_tool", label: "ship_batch", pattern: /\bship_batch\b/i },
  { id: "client:codex", kind: "client_tool", label: "Codex", pattern: /\bcodex\b/i },
  { id: "client:claude", kind: "client_tool", label: "Claude Code", pattern: /\bclaude(?: code)?\b/i },
  { id: "source:github", kind: "source", label: "GitHub", pattern: /\bgithub\b|\bpull request\b|\bcommit\b/i },
  { id: "source:slack", kind: "source", label: "Slack", pattern: /\bslack\b/i },
];

export function buildSkillToolSignals(findings: readonly WorkGraphFinding[]): WorkGraphSkillToolSignal[] {
  return SKILL_TOOL_RULES.map((rule) => {
    const matched = findings.filter((finding) =>
      rule.pattern.test(`${finding.title}\n${finding.summary}\n${finding.metadata.redacted_verbatim ?? ""}`),
    );
    if (matched.length === 0) return null;
    return {
      id: rule.id,
      label: rule.label,
      kind: rule.kind,
      mention_count: matched.length,
      source_clients: sortedUnique(matched.map((finding) => finding.source_client)),
      evidence_refs: sortedUnique(matched.map((finding) => finding.evidence_ref)).slice(0, 12),
      confidence: Number((
        matched.reduce((total, finding) => total + finding.confidence, 0) / matched.length
      ).toFixed(2)),
    } satisfies WorkGraphSkillToolSignal;
  })
    .filter((signal): signal is WorkGraphSkillToolSignal => Boolean(signal))
    .sort((left, right) => right.mention_count - left.mention_count || right.confidence - left.confidence)
    .slice(0, 12);
}

export function buildWorkGraphEvents(imports: readonly FounderLoopImport[]): WorkGraphSourceEvent[] {
  return imports.map((source, index) => ({
    schema_version: WORK_GRAPH_SCHEMA_VERSION,
    source_client: sourceClientForImport(source),
    source_id: source.sourceId,
    source_label: source.sourceLabel,
    event_type: "session_excerpt",
    text: source.text,
    evidence_ref: `${source.sourceId}:excerpt`,
    metadata: {
      import_index: index,
      line_count: source.text.split(/\r?\n/).filter(Boolean).length,
      ...(source.metadata ?? {}),
      raw_transcript_sent: false,
    },
  }));
}

export function buildWorkGraphFindings(imports: readonly FounderLoopImport[]): WorkGraphFinding[] {
  const loopFindings = extractFounderLoopItems(imports).map((item) => {
    const source = imports.find((candidate) => candidate.sourceId === item.sourceId);
    const sourceClient = source ? sourceClientForImport(source) : "unknown";
    const type = findingTypeForItem(item);
    return {
      type,
      title: titleFromText(item.text, type),
      summary: item.text,
      source_client: sourceClient,
      source_id: item.sourceId,
      evidence_ref: item.evidenceRef,
      confidence: type === "decision" || type === "artifact" ? 0.82 : 0.72,
      metadata: {
        source_label: item.sourceLabel,
        founder_loop_type: item.type,
        line_number: item.lineNumber,
      },
    } satisfies WorkGraphFinding;
  });

  const seen = new Set<string>();
  return [...loopFindings, ...buildDerivedFindings(imports)].filter((finding) => {
    const key = `${finding.type}:${finding.source_id}:${finding.summary.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildMissedOpportunities(
  coverage: WorkGraphSourceCoverage,
  findings: readonly WorkGraphFinding[],
): WorkGraphFinding[] {
  const opportunities: WorkGraphFinding[] = [];
  const hasActionableSignal = findings.some((finding) =>
    ["decision", "artifact", "blocker", "action"].includes(finding.type),
  );

  if (hasActionableSignal && !coverage.orgxMcpCalled) {
    opportunities.push({
      type: "missed_orchestration_opportunity",
      title: "High-value work happened without an OrgX MCP write",
      summary:
        "The source contains decisions, artifacts, blockers, or actions, but no durable OrgX MCP call was detected.",
      source_client: "wizard",
      source_id: "work-graph",
      evidence_ref: "work-graph:coverage:orgx-mcp",
      confidence: coverage.orgxObserved ? 0.78 : 0.7,
      metadata: {
        orgx_observed: coverage.orgxObserved,
        mcp_observed: coverage.mcpObserved,
        skill_only_signal: coverage.skillOnlySignal,
      },
    });
  }

  if (coverage.missing.length > 0) {
    opportunities.push({
      type: "missed_orchestration_opportunity",
      title: "Source coverage is incomplete",
      summary: `Missing sources: ${coverage.missing.join(", ")}.`,
      source_client: "wizard",
      source_id: "work-graph",
      evidence_ref: "work-graph:coverage:missing",
      confidence: 0.76,
      metadata: { missing_sources: coverage.missing },
    });
  }

  return opportunities;
}

function scoreOpportunity(
  coverage: WorkGraphSourceCoverage,
  findings: readonly WorkGraphFinding[],
): WorkGraphOpportunityScore {
  const count = (type: WorkGraphFindingType) => findings.filter((finding) => finding.type === type).length;
  const decisions = count("decision");
  const artifacts = count("artifact");
  const blockers = count("blocker");
  const actions = count("action");
  const people = count("person");
  const business = count("business");
  const surfaces = count("product_surface");
  const goals = count("goal");

  const evidenceQuality = clampScore(35 + Math.min(35, artifacts * 9 + decisions * 6) + Math.min(20, coverage.connected.length * 6) - coverage.missing.length * 8);
  const urgency = clampScore(20 + blockers * 18 + actions * 7 + decisions * 5);
  const ownerClarity = clampScore(25 + people * 18 + (findings.some((finding) => /\b(owner|dri|responsible|founder)\b/i.test(finding.summary)) ? 35 : 0));
  const automationPotential = clampScore(30 + actions * 10 + surfaces * 7 + (coverage.mcpObserved ? 12 : 0));
  const valuePotential = clampScore(25 + business * 20 + artifacts * 8 + goals * 10 + surfaces * 5);
  const orgxFit = clampScore(35 + decisions * 8 + blockers * 10 + artifacts * 7 + (coverage.orgxObserved ? 10 : 0) + (!coverage.orgxMcpCalled ? 10 : 0));

  return {
    overall: clampScore((valuePotential + evidenceQuality + urgency + ownerClarity + automationPotential + orgxFit) / 6),
    value_potential: valuePotential,
    evidence_quality: evidenceQuality,
    urgency,
    owner_clarity: ownerClarity,
    automation_potential: automationPotential,
    orgx_fit: orgxFit,
  };
}

function estimateImpactProjection(input: {
  coverage: WorkGraphSourceCoverage;
  findings: readonly WorkGraphFinding[];
  opportunityScore: WorkGraphOpportunityScore;
  patterns: readonly WorkGraphRecurringPattern[];
  trails: readonly WorkGraphTrail[];
}): WorkGraphImpactProjection {
  const { coverage, findings, opportunityScore, patterns, trails } = input;
  const blockerEvents = trails
    .filter((trail) => trail.subject_entity_type === "blocker")
    .reduce((total, trail) => total + Math.max(1, trail.events.length), 0);
  const decisionEvents = trails
    .filter((trail) => trail.subject_entity_type === "decision")
    .reduce((total, trail) => total + Math.max(1, trail.events.length), 0);
  const artifactEvents = trails
    .filter((trail) => trail.subject_entity_type === "artifact")
    .reduce((total, trail) => total + Math.max(1, trail.events.length), 0);
  const missedCount = findings.filter((finding) => finding.type === "missed_orchestration_opportunity").length;
  const repeatedEvents = trails
    .filter((trail) => trail.events.length > 1)
    .reduce((total, trail) => total + trail.events.length, 0);
  const rawHours =
    blockerEvents * 0.75 +
    decisionEvents * 0.45 +
    artifactEvents * 0.25 +
    missedCount * 0.65 +
    repeatedEvents * 0.35 +
    coverage.missing.length * 0.4;
  const hours = Number(Math.max(0.5, Math.min(40, rawHours)).toFixed(1));
  const acceleration = clampScore(
    6 +
      Math.round(opportunityScore.automation_potential * 0.22) +
      patterns.filter((pattern) => pattern.severity === "critical" || pattern.severity === "high").length * 5 +
      (coverage.manifests?.filter((manifest) => manifest.status === "connected").length ?? 0) * 2,
  );
  const confidenceInputs = [
    ...findings.map((finding) => finding.confidence),
    ...patterns.map((pattern) => pattern.confidence),
    coverage.coverage_score ? coverage.coverage_score / 100 : 0.55,
  ];
  const confidence = Number((
    confidenceInputs.reduce((total, value) => total + value, 0) / Math.max(1, confidenceInputs.length)
  ).toFixed(2));

  return {
    time_saved_hours_per_week: hours,
    acceleration_percent: Math.min(70, acceleration),
    estimated_monthly_value_usd: Math.round(hours * 4.33 * 200),
    confidence,
    basis: [
      `${blockerEvents} blocker event${blockerEvents === 1 ? "" : "s"} at 45 minutes of reconstruction/coordination each.`,
      `${decisionEvents} decision event${decisionEvents === 1 ? "" : "s"} at 27 minutes of rediscovery/promotion each.`,
      `${missedCount} missed orchestration signal${missedCount === 1 ? "" : "s"} at 39 minutes of manual writeback each.`,
      `${coverage.missing.length} source gap${coverage.missing.length === 1 ? "" : "s"} increasing the AQ ceiling rather than lowering the current score.`,
    ],
    assumptions: [
      "Uses a conservative $200/hour blended founder/operator cost for public-safe value estimates.",
      "Acceleration estimates the execution lift from turning repeated evidence into automated attribution, decisions, and source connections.",
      "Impact is directional until the user claims the fingerprint and confirms or corrects the evidence.",
    ],
  };
}

function scoreExecutionQuality(input: {
  coverage: WorkGraphSourceCoverage;
  findings: readonly WorkGraphFinding[];
  impact: WorkGraphImpactProjection;
  patterns: readonly WorkGraphRecurringPattern[];
  recommendations: readonly WorkGraphTrailRecommendation[];
  trails: readonly WorkGraphTrail[];
}): WorkGraphExecutionQualityScore {
  const { coverage, findings, impact, patterns, recommendations, trails } = input;
  const uniqueClients = sortedUnique(findings.map((finding) => finding.source_client));
  const unknownCount = findings.filter((finding) => finding.source_client === "unknown").length;
  const multiEventTrails = trails.filter((trail) => trail.events.length > 1).length;
  const evidenceCoverage = clampScore(
    (coverage.coverage_score ?? 50) +
      Math.min(18, coverage.connected.length * 3),
  );
  const sourceAttribution = clampScore(
    34 +
      uniqueClients.filter((client) => client !== "unknown").length * 10 +
      (coverage.manifests?.filter((manifest) => manifest.status === "connected").length ?? 0) * 6 -
      unknownCount * 8,
  );
  const trailDepth = clampScore(
    25 +
      (trails.length > 0 ? Math.round((multiEventTrails / trails.length) * 45) : 0) +
      Math.min(20, trails.reduce((total, trail) => total + trail.events.length, 0) * 2),
  );
  const insightDepth = clampScore(
    30 +
      Math.min(28, patterns.length * 8) +
      Math.min(18, findings.filter((finding) => ["blocker", "decision", "outcome", "missed_orchestration_opportunity"].includes(finding.type)).length * 3),
  );
  const actionability = clampScore(
    30 +
      Math.min(35, recommendations.length * 10) +
      Math.min(20, recommendations.filter((recommendation) => recommendation.priority === "p0").length * 10),
  );
  const impactConfidence = clampScore(impact.confidence * 100);
  const overall = clampScore(
    evidenceCoverage * 0.22 +
      sourceAttribution * 0.18 +
      trailDepth * 0.18 +
      insightDepth * 0.18 +
      actionability * 0.14 +
      impactConfidence * 0.1,
  );

  return {
    overall,
    evidence_coverage: evidenceCoverage,
    source_attribution: sourceAttribution,
    trail_depth: trailDepth,
    insight_depth: insightDepth,
    actionability,
    impact_confidence: impactConfidence,
    notes: [
      "10/10 requires client-native extraction per source, multi-event chronology, source-specific evidence, and action recommendations tied to impact.",
      multiEventTrails === trails.length
        ? "Every evidence path has multiple events."
        : `${trails.length - multiEventTrails} finding${trails.length - multiEventTrails === 1 ? "" : "s"} still need more chronology before they should be called recurring.`,
      coverage.missing.length > 0
        ? `AQ ceiling sources not connected yet: ${coverage.missing.join(", ")}.`
        : "No required source gaps were declared for this run.",
    ],
  };
}

function scorePageQuality(input: {
  auditMethod: WorkGraphAuditMethod;
  coverage: WorkGraphSourceCoverage;
  impact: WorkGraphImpactProjection;
  mirror: WorkGraphMirror;
  recommendations: readonly WorkGraphTrailRecommendation[];
  trails: readonly WorkGraphTrail[];
}): WorkGraphPageQualityScore {
  const { auditMethod, coverage, impact, mirror, recommendations, trails } = input;
  const body = `${mirror.headline}\n${mirror.body}\n${mirror.claims.map((claim) => claim.text).join("\n")}`;
  const lowerBody = body.toLowerCase();
  const namedMissingSources = coverage.missing.filter((source) =>
    lowerBody.includes(source.toLowerCase()) || lowerBody.includes(source.toLowerCase().replace(/ pr\/commit proof/i, ""))
  ).length;
  const evidenceRefs = new Set(mirror.claims.flatMap((claim) => claim.evidence_refs));
  const topTrail = selectPublicTopTrail(trails);
  const hasSpecificShape =
    /\b\d+ finding/.test(body) &&
    /\b\d+ decision/.test(body) &&
    /\b\d+ artifact/.test(body);
  const hasReaderFrame = /\byou\b|\byour\b/i.test(body);
  const hasPrivacyFrame = /\braw transcripts?\b/i.test(body);
  const hasImpactFrame =
    impact.time_saved_hours_per_week > 0 &&
    /\brecoverable hours\/week\b|\bhours\/week\b/i.test(body) &&
    /\$[0-9,]+\/month\b/i.test(body);
  const hasRepairFrame =
    recommendations.length > 0 &&
    /\bnext repair\b|\blinked proof\b|\bowner-visible\b/i.test(body);
  const hasGapFrame =
    coverage.missing.length === 0 ||
    (/\bmissing proof\b|\bsource gap\b|\bproof chain\b/i.test(body) && namedMissingSources > 0);
  const awkwardCopyPenalty = [
    /\ba ongoing\b/i,
    /\[brief impact\]/i,
    /\bdetected across\b/i,
    /\bno evidence refs\b/i,
  ].filter((pattern) => pattern.test(body)).length * 8;

  const clarity = clampScore(
    72 +
      (mirror.headline.length >= 24 && mirror.headline.length <= 96 ? 8 : 0) +
      (hasSpecificShape ? 10 : 0) +
      (topTrail ? 6 : 0) +
      (auditMethod.retained_evidence_lines > 0 ? 4 : 0) +
      (hasGapFrame ? 6 : 0) -
      awkwardCopyPenalty,
  );
  const pageImpact = clampScore(
    64 +
      (hasImpactFrame ? 16 : 0) +
      (impact.estimated_monthly_value_usd > 0 ? 7 : 0) +
      (recommendations.length > 0 ? 8 : 0) +
      (topTrail?.impact_score && topTrail.impact_score >= 60 ? 5 : 0),
  );
  const resonance = clampScore(
    66 +
      (hasReaderFrame ? 10 : 0) +
      (/\boperating leak\b|\bbusiness system\b|\bproof chain\b/i.test(body) ? 10 : 0) +
      (coverage.connected.length >= 2 ? 4 : 0) +
      (trails.length >= 8 ? 6 : 0),
  );
  const trust = clampScore(
    62 +
      (hasPrivacyFrame ? 10 : 0) +
      (hasGapFrame ? 12 : 0) +
      Math.min(10, evidenceRefs.size * 2) +
      Math.min(6, namedMissingSources * 2),
  );
  const repairReadiness = clampScore(
    62 +
      (hasRepairFrame ? 14 : 0) +
      Math.min(12, recommendations.length * 4) +
      (recommendations.some((recommendation) => recommendation.priority === "p0") ? 8 : 0) +
      (coverage.missing.length > 0 && hasGapFrame ? 4 : 0),
  );
  const overall = clampScore(
    clarity * 0.24 +
      pageImpact * 0.22 +
      resonance * 0.2 +
      trust * 0.18 +
      repairReadiness * 0.16,
  );

  return {
    overall,
    clarity,
    impact: pageImpact,
    resonance,
    trust,
    repair_readiness: repairReadiness,
    notes: [
      "This rates the quality of the public readout, not the underlying work evidence score.",
      hasGapFrame
        ? "The readout names the proof gaps instead of hiding them."
        : "The readout should name the missing proof sources more explicitly.",
      hasRepairFrame
        ? "The page gives a concrete repair path tied to proof and ownership."
        : "The page needs a sharper next repair tied to owner-visible proof.",
    ],
  };
}

function buildAgenticQuotient(input: {
  auditMethod: WorkGraphAuditMethod;
  coverage: WorkGraphSourceCoverage;
  executionQuality: WorkGraphExecutionQualityScore;
  findings: readonly WorkGraphFinding[];
  recommendations: readonly WorkGraphTrailRecommendation[];
  trails: readonly WorkGraphTrail[];
}): WorkGraphAgenticQuotient {
  const { auditMethod, coverage, executionQuality, findings, recommendations, trails } = input;
  const connectedLabels = sortedUnique(coverage.connected);
  const connectedClients = sortedUnique(findings.map((finding) => finding.source_client).filter((client) => client !== "unknown"));
  const aiClientCount = connectedClients.filter((client) =>
    ["codex", "claude", "claude-code", "cursor", "opencode", "goose"].includes(client)
  ).length;
  const proofSourceCount = connectedClients.filter((client) =>
    ["github", "slack", "mcp", "orgx_runtime_hook"].includes(client)
  ).length;
  const toolSignalCount = findings.filter((finding) =>
    finding.type === "missed_orchestration_opportunity" ||
    /\b(?:mcp__|tool|agent|skill|recipe|workflow)\b/i.test(`${finding.title}\n${finding.summary}`)
  ).length;
  const decisionCount = findings.filter((finding) => finding.type === "decision").length;
  const artifactCount = findings.filter((finding) => finding.type === "artifact").length;
  const outcomeCount = findings.filter((finding) => finding.type === "outcome").length;
  const blockerCount = findings.filter((finding) => finding.type === "blocker").length;
  const verifiedTrailCount = trails.filter((trail) =>
    trail.state === "verified" ||
    trail.valence === "healthy" ||
    trail.events.some((event) =>
      event.event_type === "artifact_verified" ||
      event.event_type === "outcome_recorded" ||
      event.source_type === "github" ||
      event.source_type === "orgx_runtime_hook" ||
      event.source_type === "mcp"
    )
  ).length;
  const multiEventTrailCount = trails.filter((trail) => trail.events.length > 1).length;
  const sourceBonus = clampScore(
    Math.min(18, connectedLabels.length * 3) +
      aiClientCount * 3 +
      proofSourceCount * 4,
  );
  const rawStackScore = clampScore(
    24 +
      sourceBonus +
      Math.min(18, auditMethod.searched_session_files / 8) +
      Math.min(14, toolSignalCount * 2) +
      Math.min(12, findings.length) +
      (coverage.mcpObserved ? 5 : 0),
  );
  const rawDurabilityScore = clampScore(
    30 +
      Math.min(14, decisionCount * 3) +
      Math.min(14, artifactCount * 3) +
      Math.min(12, outcomeCount * 4) +
      Math.min(12, verifiedTrailCount * 4) +
      Math.min(10, multiEventTrailCount * 2) +
      (coverage.orgxMcpCalled ? 8 : 0) +
      Math.min(8, recommendations.length * 2),
  );

  // Honesty ceiling — a corpus where the critic dropped findings, or chronology
  // is shallow, must not be allowed to score 100. The score is bounded by:
  //   honestyMultiplier = (0.5 + 0.5 * criticPass) * (0.55 + 0.45 * depth/100)
  // floored at 0.35 so we never zero out a credible report.
  // criticPass proxies from trails: multi-event chronology + verified evidence.
  // chronology depth comes from the existing executionQuality.trail_depth (0-100).
  const totalTrails = trails.length;
  const trailPassesCritic = (trail: WorkGraphTrail): boolean =>
    trail.events.length >= 2 &&
    (trail.state === "verified" ||
      trail.valence === "healthy" ||
      trail.events.some((event) =>
        event.event_type === "artifact_verified" ||
        event.event_type === "outcome_recorded" ||
        event.source_type === "github" ||
        event.source_type === "orgx_runtime_hook" ||
        event.source_type === "mcp",
      ));
  const passingTrails = trails.filter(trailPassesCritic).length;
  const criticPassRatio = totalTrails > 0 ? passingTrails / totalTrails : 0.35;
  const chronologyDepth = executionQuality.trail_depth;
  const chronologyFactor = 0.55 + 0.45 * (Math.max(0, Math.min(100, chronologyDepth)) / 100);
  const criticFactor = 0.5 + 0.5 * Math.max(0, Math.min(1, criticPassRatio));
  const honestyMultiplier = Math.max(0.35, Math.min(1, criticFactor * chronologyFactor));

  // Stack is penalized less than durability because stack measures connection
  // breadth (a real signal even when evidence is shallow), while durability is
  // about whether decisions actually survived as durable proof.
  const stackScore = clampScore(rawStackScore * (0.7 + 0.3 * honestyMultiplier));
  const durabilityScore = clampScore(rawDurabilityScore * honestyMultiplier);
  const aq = clampScore(stackScore * 0.3 + durabilityScore * 0.7);
  const repairQuests = buildAgenticRepairQuests({
    coverage,
    findings,
    recommendations,
  });
  const repairLift = repairQuests.reduce((total, quest) => total + quest.expected_aq_lift, 0);
  const ceiling = clampScore(aq + Math.min(36, repairLift));
  const agenticGap = Math.max(0, ceiling - aq);
  const contextLeakScore = clampScore(
    blockerCount * 10 +
      findings.filter((finding) => finding.type === "missed_orchestration_opportunity").length * 8 +
      trails.filter((trail) => trail.state === "blocked" || trail.state === "stale" || trail.valence === "risk").length * 4,
  );
  const archetype = assignAgenticArchetype({
    stackScore,
    durabilityScore,
    contextLeakScore,
    agenticGap,
    criticPassRatio,
    coverage,
    findings,
  });
  const tiers = computeTiers({ stackScore, durabilityScore });

  return {
    aq,
    stack_score: stackScore,
    durability_score: durabilityScore,
    agentic_gap: agenticGap,
    ceiling,
    source_bonus: sourceBonus,
    context_leak_score: contextLeakScore,
    archetype,
    tiers,
    repair_quests: repairQuests,
    notes: [
      "AQ is source-positive: connected sources, proof, and runtime writeback raise the current score; missing sources raise the ceiling and repair quests instead of lowering the current score.",
      `Audit quality remains separate at ${executionQuality.overall}/100 so the public page can be compelling without inflating evidence truth.`,
      `Honesty ceiling applied: critic-pass ${Math.round(criticPassRatio * 100)}% × chronology depth ${chronologyDepth}/100 caps the score at ${Math.round(100 * honestyMultiplier)}.`,
    ],
  };
}

function buildAgenticRepairQuests(input: {
  coverage: WorkGraphSourceCoverage;
  findings: readonly WorkGraphFinding[];
  recommendations: readonly WorkGraphTrailRecommendation[];
}): WorkGraphAgenticQuotient["repair_quests"] {
  const quests: WorkGraphAgenticQuotient["repair_quests"] = [];
  const add = (quest: WorkGraphAgenticQuotient["repair_quests"][number]) => {
    if (quests.some((existing) => existing.id === quest.id)) return;
    quests.push(quest);
  };
  for (const gap of input.coverage.missing) {
    if (/github|commit|pr/i.test(gap)) {
      add({
        id: "connect-github-proof",
        title: "Connect GitHub proof",
        expected_aq_lift: 12,
        reason: "Attach commits, PRs, reviews, and CI state to decisions and artifacts.",
        source_gap: gap,
        action_type: "connect_source",
      });
    } else if (/slack/i.test(gap)) {
      add({
        id: "connect-slack-coordination",
        title: "Connect Slack coordination",
        expected_aq_lift: 9,
        reason: "Attach owner, approval, and handoff context to the work graph.",
        source_gap: gap,
        action_type: "connect_source",
      });
    } else if (/runtime|hook|outbox/i.test(gap)) {
      add({
        id: "enable-runtime-hooks",
        title: "Enable runtime hooks",
        expected_aq_lift: 18,
        reason: "Convert agent work into durable OrgX writeback without relying on humans to remember reporting.",
        source_gap: gap,
        action_type: "enable_runtime_hook",
      });
    } else {
      add({
        id: `connect-${shortHash(gap, 8)}`,
        title: `Connect ${gap}`,
        expected_aq_lift: 5,
        reason: "Add another proof surface to increase confidence and source confluence.",
        source_gap: gap,
        action_type: "connect_source",
      });
    }
  }
  if (input.findings.some((finding) => finding.type === "decision")) {
    add({
      id: "promote-trapped-decisions",
      title: "Promote trapped decisions",
      expected_aq_lift: 7,
      reason: "Move decisions from session text into durable OrgX records with evidence refs.",
      action_type: "promote_decisions",
    });
  }
  if (input.findings.some((finding) => finding.type === "artifact")) {
    add({
      id: "attach-artifact-proof",
      title: "Attach proof to top artifacts",
      expected_aq_lift: 6,
      reason: "Link artifacts to commits, tests, deploys, or review proof.",
      action_type: "attach_proof",
    });
  }
  for (const recommendation of input.recommendations.slice(0, 1)) {
    add({
      id: `launch-${recommendation.id}`,
      title: recommendation.title,
      expected_aq_lift: recommendation.priority === "p0" ? 8 : 5,
      reason: recommendation.summary,
      action_type: "launch_repair",
    });
  }
  return quests.sort((left, right) => right.expected_aq_lift - left.expected_aq_lift).slice(0, 6);
}

// Two-axis tier classification — Stack Usage × Durable Work.
// Both axes share a 6-tier ladder, but the labels differ because the axes
// answer different questions: "how rich is the AI stack" vs "how durable does
// the work become." Hope's AQ 50 / Stack 73 / Durable 40 maps to:
//   stack:   tier 5  · "AI-Maxed"
//   durable: tier 2  · "Context Leaking"
//
// Labels were chosen to be:
//   - Plain English (a stranger can read "Context Leaking" and grok it).
//   - Time-resistant (no 2026-specific memes; avoids "Agent Swarm Mode").
//   - Compose-able with the memetic archetype subtype above.
type TierLevel = 1 | 2 | 3 | 4 | 5 | 6;
const STACK_TIER_LABELS: Record<TierLevel, string> = {
  1: "Prompt Tourist",
  2: "Assisted",
  3: "Operator",
  4: "AI-Forward",
  5: "AI-Maxed",
  6: "Distributed Agent",
};
const DURABLE_TIER_LABELS: Record<TierLevel, string> = {
  1: "Transcript Graveyard",
  2: "Context Leaking",
  3: "Partial Memory",
  4: "Durable",
  5: "Compounding",
  6: "Compounding Org",
};

function stackTier(score: number): TierLevel {
  if (score < 25) return 1;
  if (score < 45) return 2;
  if (score < 55) return 3;
  if (score < 70) return 4;
  if (score < 85) return 5;
  return 6;
}
function durableTier(score: number): TierLevel {
  if (score < 25) return 1;
  if (score < 45) return 2;
  if (score < 60) return 3;
  if (score < 75) return 4;
  if (score < 90) return 5;
  return 6;
}
function computeTiers(input: { stackScore: number; durabilityScore: number }): WorkGraphAgenticQuotient["tiers"] {
  const sTier = stackTier(input.stackScore);
  const dTier = durableTier(input.durabilityScore);
  return {
    stack: { tier: sTier, label: STACK_TIER_LABELS[sTier] },
    durable: { tier: dTier, label: DURABLE_TIER_LABELS[dTier] },
  };
}

// Plain-English primary lines paired with each memetic archetype subtype.
// The primary reads to a stranger; the subtype carries the share value.
const ARCHETYPE_PRIMARIES: Record<NonNullable<WorkGraphAgenticQuotient["archetype"]>["id"], string> = {
  mcp_necromancer: "Tool-Rich, Memory-Poor",
  ai_native_operator: "Connected & Durable",
  proof_maximalist: "Heavy on Receipts",
  context_leaker: "Tools In, Proof Out",
  agent_wrangler: "Agents Without Ops",
  decision_ghost: "Decisions Without Records",
  careful_operator: "Small Stack, Tight Loop",
  prompt_tourist: "Early Days",
};

// Hard-coded archetype table. Priority order: most-specific match wins first.
// The table is documented in the launch readiness spec — if you change a range,
// update both the comment AND the fixture tests in work-graph.test.ts.
//
//   AI-Native Operator: Stack ≥ 70 AND Durable ≥ 70 AND Gap ≤ 15
//   Proof Maximalist:   Durable ≥ 80 AND criticPass ≥ 0.85 AND contextLeak ≤ 20
//   MCP Necromancer:    Stack ≥ 70 AND Durable ≤ 50 AND hauntingSignals ≥ 3
//   Context Leaker:     Stack ≥ 70 AND Durable ∈ (50, 65)
//   Agent Wrangler:     agentSignalCount ≥ 3 AND Durable < 65
//   Decision Ghost:     decisionCount ≥ 2 AND Durable < 62
//   Careful Operator:   Stack < 60 AND Durable ≥ 68
//   Prompt Tourist:     default
function assignAgenticArchetype(input: {
  stackScore: number;
  durabilityScore: number;
  contextLeakScore: number;
  agenticGap: number;
  criticPassRatio: number;
  coverage: WorkGraphSourceCoverage;
  findings: readonly WorkGraphFinding[];
}): WorkGraphAgenticQuotient["archetype"] {
  const { stackScore, durabilityScore, contextLeakScore, agenticGap, criticPassRatio } = input;
  const toolFailureCount = input.findings.filter((finding) =>
    finding.type === "blocker" && /\b(?:mcp|schema|tool|zod|invalid)\b/i.test(`${finding.title}\n${finding.summary}`)
  ).length;
  const hauntingSignals = toolFailureCount + (input.coverage.mcpObserved ? 1 : 0) + (contextLeakScore >= 40 ? 1 : 0);
  const agentSignalCount = input.findings.filter((finding) =>
    /\b(?:agent|subagent|delegate|orchestrator|worker)\b/i.test(`${finding.title}\n${finding.summary}`)
  ).length;
  const decisionCount = input.findings.filter((finding) => finding.type === "decision").length;

  if (stackScore >= 70 && durabilityScore >= 70 && agenticGap <= 15) {
    return {
      id: "ai_native_operator",
      label: "AI-Native Operator",
      primary: ARCHETYPE_PRIMARIES.ai_native_operator,
      roast: "The agents are working and the receipts mostly survive contact with reality.",
      truth: "Your work is both agent-rich and increasingly durable.",
      repair: "Scale the loop into weekly deltas and team-level source coverage.",
    };
  }
  if (durabilityScore >= 80 && criticPassRatio >= 0.85 && contextLeakScore <= 20) {
    return {
      id: "proof_maximalist",
      label: "Proof Maximalist",
      primary: ARCHETYPE_PRIMARIES.proof_maximalist,
      roast: "Annoyingly competent. Receipts attached.",
      truth: "Verification and ownership are the strong parts of your system.",
      repair: "Use the proof base to widen the agent stack.",
    };
  }
  if (stackScore >= 70 && durabilityScore <= 50 && hauntingSignals >= 3) {
    return {
      id: "mcp_necromancer",
      label: "MCP Necromancer",
      primary: ARCHETYPE_PRIMARIES.mcp_necromancer,
      roast: "You summoned the tools; a few still need contracts.",
      truth: "Your stack is rich, but repeated tool failures are leaking context.",
      repair: "Add runtime hook replay and tool-contract proof before expanding the stack.",
    };
  }
  if (stackScore >= 70 && durabilityScore > 50 && durabilityScore < 65) {
    return {
      id: "context_leaker",
      label: "Context Leaker",
      primary: ARCHETYPE_PRIMARIES.context_leaker,
      roast: "Your stack is cooking. Your operating memory is evaporating.",
      truth: "A lot is happening, but too much of it dies in sessions.",
      repair: "Enable runtime hooks, connect proof sources, and promote trapped decisions.",
    };
  }
  if (agentSignalCount >= 3 && durabilityScore < 65) {
    return {
      id: "agent_wrangler",
      label: "Agent Wrangler",
      primary: ARCHETYPE_PRIMARIES.agent_wrangler,
      roast: "You have agents. They do not yet have an operating system.",
      truth: "Delegation is happening, but coordination proof is thin.",
      repair: "Promote owner-visible workstreams and attach proof to agent handoffs.",
    };
  }
  if (decisionCount >= 2 && durabilityScore < 62) {
    return {
      id: "decision_ghost",
      label: "Decision Ghost",
      primary: ARCHETYPE_PRIMARIES.decision_ghost,
      roast: "Your best calls are haunting transcripts instead of steering work.",
      truth: "Decision evidence exists, but durable decision records are weak.",
      repair: "Promote decisions with commit, owner, and outcome refs.",
    };
  }
  if (stackScore < 60 && durabilityScore >= 68) {
    return {
      id: "careful_operator",
      label: "Careful Operator",
      primary: ARCHETYPE_PRIMARIES.careful_operator,
      roast: "Small stack, strong receipts.",
      truth: "You use fewer agents, but the work you do run tends to compound.",
      repair: "Add one new client or proof source without weakening durability.",
    };
  }
  return {
    id: "prompt_tourist",
    label: "Prompt Tourist",
    primary: ARCHETYPE_PRIMARIES.prompt_tourist,
    roast: "You are early enough that the score is still honest, not embarrassing.",
    truth: "There is not enough durable agent work yet to call this a compounding system.",
    repair: "Pick one client, run real work through it, then connect proof.",
  };
}

function inferFinalState(findings: readonly WorkGraphFinding[]): WorkGraphFinalState {
  if (findings.some((finding) => finding.type === "blocker")) return "blocked";
  if (findings.some((finding) => finding.type === "artifact" || finding.type === "outcome" || /\b(shipped|completed|done|verified)\b/i.test(finding.summary))) {
    return "completed";
  }
  if (findings.some((finding) => finding.type === "action" || finding.type === "decision")) return "in_progress";
  return "unknown";
}

function buildKickoffs(
  findings: readonly WorkGraphFinding[],
  missed: readonly WorkGraphFinding[],
  score: WorkGraphOpportunityScore,
): WorkGraphKickoffCandidate[] {
  const blockers = findings.filter((finding) => finding.type === "blocker").slice(0, 3);
  const decisions = findings.filter((finding) => finding.type === "decision").slice(0, 3);
  const artifacts = findings.filter((finding) => finding.type === "artifact").slice(0, 3);
  const kickoffs: WorkGraphKickoffCandidate[] = [];

  if (missed.length > 0) {
    kickoffs.push({
      title: "Install continuous OrgX writeback",
      summary: "Turn detected work into live OrgX activity, retro, outcome, and decision records.",
      reason: missed[0]?.summary ?? "A source produced work without durable OrgX orchestration.",
      finding_refs: missed.map((finding) => finding.evidence_ref).slice(0, 4),
      priority: score.overall >= 75 ? "p0" : "p1",
    });
  }

  if (blockers.length > 0 || decisions.length > 0) {
    kickoffs.push({
      title: "Resolve the hidden decision queue",
      summary: "Convert unresolved decisions and blockers into owner-visible OrgX decision cards.",
      reason: "The Work Graph found decisions or blockers that can slow execution if they stay buried in transcripts.",
      finding_refs: [...blockers, ...decisions].map((finding) => finding.evidence_ref).slice(0, 5),
      priority: blockers.length > 0 ? "p0" : "p1",
    });
  }

  if (artifacts.length > 0) {
    kickoffs.push({
      title: "Attach proof to the operating loop",
      summary: "Promote completed work into artifacts with owners, evidence refs, and next actions.",
      reason: "Completed work was detected; OrgX can make it queryable and reusable.",
      finding_refs: artifacts.map((finding) => finding.evidence_ref),
      priority: "p1",
    });
  }

  if (kickoffs.length === 0) {
    kickoffs.push({
      title: "Connect first work source",
      summary: "Add AI client, Slack, or MCP source coverage so OrgX can build a useful Work Graph.",
      reason: "The scan did not find enough structured work to recommend a specific operating initiative.",
      finding_refs: [],
      priority: "p2",
    });
  }

  return kickoffs.slice(0, 3);
}

function entityTypeForFinding(finding: WorkGraphFinding): WorkGraphEntityType {
  switch (finding.type) {
    case "decision":
      return "decision";
    case "artifact":
      return "artifact";
    case "blocker":
      return "blocker";
    case "person":
      return "person";
    case "business":
      return "business";
    case "product_surface":
      return "surface";
    case "goal":
    case "initiative_candidate":
      return "initiative";
    case "outcome":
      return "outcome";
    case "action":
      return /\b(outcome|shipped|completed|verified)\b/i.test(finding.summary) ? "outcome" : "task";
    case "missed_orchestration_opportunity":
      return "source";
  }
}

function trailKindForEntity(entityType: WorkGraphEntityType): WorkGraphTrailKind {
  switch (entityType) {
    case "decision":
      return "decision_trail";
    case "artifact":
      return "artifact_trail";
    case "person":
      return "person_trail";
    case "business":
      return "business_trail";
    case "blocker":
      return "blocker_trail";
    case "agent":
      return "agent_trail";
    case "tool":
    case "source":
      return "source_trail";
    case "outcome":
      return "outcome_trail";
    case "idea":
      return "idea_trail";
    case "initiative":
    case "workstream":
    case "milestone":
    case "task":
    case "surface":
      return "initiative_trail";
  }
}

function eventTypeForFinding(finding: WorkGraphFinding): WorkGraphTrailEvent["event_type"] {
  switch (finding.type) {
    case "decision":
      return "decision_inferred";
    case "artifact":
      return /\b(verified|proof|tested|passed)\b/i.test(finding.summary)
        ? "artifact_verified"
        : "artifact_created";
    case "blocker":
      return "blocker_detected";
    case "person":
      return "owner_assigned";
    case "business":
      return "signal_detected";
    case "product_surface":
      return "signal_detected";
    case "goal":
    case "initiative_candidate":
      return "initiative_created";
    case "outcome":
      return "outcome_recorded";
    case "action":
      return /\b(outcome|result|impact|roi)\b/i.test(finding.summary)
        ? "outcome_recorded"
        : "recommendation_generated";
    case "missed_orchestration_opportunity":
      return "source_connected";
  }
}

function trailStateForFindings(findings: readonly WorkGraphFinding[]): WorkGraphTrailState {
  if (findings.some((finding) => finding.type === "blocker")) return "blocked";
  if (findings.some((finding) => finding.type === "missed_orchestration_opportunity")) return "missing_evidence";
  if (findings.some((finding) => /\b(contradict|conflict)\b/i.test(finding.summary))) return "contradicted";
  if (findings.some((finding) => finding.type === "outcome" || /\b(verified|proof|passed|complete_with_proof)\b/i.test(finding.summary))) return "verified";
  if (findings.some((finding) => finding.type === "decision")) return "inferred";
  return "observed";
}

function trailValenceForFindings(findings: readonly WorkGraphFinding[], recurrence: number): WorkGraphTrailValence {
  if (findings.some((finding) => finding.type === "blocker")) return recurrence > 1 ? "escalating" : "risk";
  if (findings.some((finding) => finding.type === "missed_orchestration_opportunity")) return "leak";
  if (findings.some((finding) => finding.type === "decision") && recurrence > 1) return "wasteful_recurrence";
  if (findings.some((finding) => finding.type === "outcome")) return "healthy";
  if (findings.some((finding) => finding.type === "business")) return "opportunity";
  if (findings.some((finding) => finding.type === "artifact")) return "healthy";
  return recurrence > 1 ? "useful_recurrence" : "opportunity";
}

function trailShapeForFindings(findings: readonly WorkGraphFinding[], recurrence: number): WorkGraphTrail["shape"] {
  const decisions = findings.filter((finding) => finding.type === "decision").length;
  const artifacts = findings.filter((finding) => finding.type === "artifact").length;
  if (recurrence <= 1) return "single_signal";
  if (decisions > artifacts && decisions > 0) return "decision_heavy_artifact_light";
  if (artifacts > decisions && artifacts > 0 && decisions === 0) return "artifact_heavy_decision_light";
  if (findings.some((finding) => finding.type === "blocker")) return recurrence >= 3 ? "accelerating_issue" : "dense_recent_cluster";
  if (recurrence >= 3) return "chronic_recurrence";
  if (findings.some((finding) => /\b(resurface|again|back|revived|reappeared)\b/i.test(finding.summary))) return "zombie_revival";
  if (findings.some((finding) => finding.type === "outcome" || /\b(verified|passed|shipped|completed)\b/i.test(finding.summary))) return "healthy_execution";
  return "dense_recent_cluster";
}

function findingTimestamp(finding: WorkGraphFinding, fallback: string): string {
  const occurredAt = finding.metadata.occurred_at;
  return isIsoTimestamp(occurredAt) ? occurredAt : fallback;
}

function canonicalRelatedToken(finding: WorkGraphFinding): string | null {
  const candidates = [
    ...arrayOfStrings(finding.metadata.related_source_ids),
    ...arrayOfStrings(finding.metadata.related_entity_names),
    finding.source_id,
    finding.title,
  ];
  const scored = candidates
    .map((candidate) => candidate.trim())
    .filter(Boolean)
    .map((candidate) => {
      const normalized = candidate.toLowerCase();
      let score = 0;
      if (/_/.test(candidate)) score += 6;
      if (/orgx|mcp|wizard|plugin|runtime|hook|work graph|profile|trace|scaffold|ship|spawn|record|submit|list/i.test(candidate)) score += 5;
      if (/session|jsonl|rollout|line|current-release/i.test(normalized)) score -= 4;
      if (candidate.length > 80) score -= 2;
      return { candidate, score };
    })
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score);
  return scored[0]?.candidate ?? null;
}

function groupingKeyForFinding(finding: WorkGraphFinding): string {
  const entityType = entityTypeForFinding(finding);
  const token = canonicalRelatedToken(finding);
  if (token && ["blocker", "source", "surface", "initiative", "outcome", "task"].includes(entityType)) {
    return `${entityType}:${slugPart(token)}`;
  }
  return `${entityType}:${slugPart(finding.title)}`;
}

function trailTitleForGroup(entityType: WorkGraphEntityType, group: readonly WorkGraphFinding[]): string {
  const token = canonicalRelatedToken(group[0]!);
  if (group.length > 1 && token) {
    const label = token.replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    if (entityType === "blocker") return `${label} is recurring as an execution blocker`;
    if (entityType === "source") return `${label} is an attribution coverage gap`;
    if (entityType === "surface") return `${label} is becoming a product surface evidence path`;
    if (entityType === "outcome") return `${label} is becoming outcome evidence`;
    return `${label} is becoming durable work`;
  }
  return group[0]!.title;
}

function trailSummaryForGroup(group: readonly WorkGraphFinding[]): string {
  if (group.length === 1) return group[0]!.summary;
  const sources = sortedUnique(group.map((finding) => labelForSourceClient(finding.source_client)));
  const states = sortedUnique(
    group
      .map((finding) => typeof finding.metadata.state === "string" ? finding.metadata.state : "")
      .filter(Boolean),
  );
  return [
    `${group.length} evidence events connect this finding across ${sources.join(", ")}.`,
    states.length > 0 ? `Observed states: ${states.join(", ")}.` : "",
    group[0]?.summary ?? "",
  ].filter(Boolean).join(" ");
}

export function buildWorkGraphTrails(
  findings: readonly WorkGraphFinding[],
  generatedAt: string,
): WorkGraphTrail[] {
  const grouped = new Map<string, WorkGraphFinding[]>();

  for (const finding of findings) {
    const key = groupingKeyForFinding(finding);
    grouped.set(key, [...(grouped.get(key) ?? []), finding]);
  }

  return [...grouped.entries()].map(([key, group], index) => {
    const sortedGroup = [...group].sort((left, right) =>
      findingTimestamp(left, generatedAt).localeCompare(findingTimestamp(right, generatedAt))
    );
    const first = sortedGroup[0]!;
    const entityType = entityTypeForFinding(first);
    const entityId = `${entityType}:${shortHash(key, 12)}`;
    const trailId = `trail:${shortHash({ key, evidence: sortedGroup.map((finding) => finding.evidence_ref) }, 14)}`;
    const recurrence = sortedGroup.length;
    const evidenceRefs = sortedUnique(sortedGroup.map((finding) => finding.evidence_ref));
    const events: WorkGraphTrailEvent[] = sortedGroup.map((finding, eventIndex) => ({
      id: `${trailId}:event:${eventIndex + 1}`,
      trail_id: trailId,
      event_type: eventTypeForFinding(finding),
      entity_id: entityId,
      entity_type: entityType,
      timestamp: findingTimestamp(finding, generatedAt),
      ...(typeof finding.metadata.actor_id === "string" ? { actor_id: finding.metadata.actor_id } : {}),
      source_id: finding.source_id,
      source_type: finding.source_client,
      redacted_verbatim: finding.summary.slice(0, 320),
      confidence: finding.confidence,
      evidence_refs: [finding.evidence_ref],
      privacy_state: "redacted",
    }));
    const edges: WorkGraphTrailEdge[] = events.slice(1).map((event, edgeIndex) => ({
      id: `${trailId}:edge:${edgeIndex + 1}`,
      from_event_id: events[edgeIndex]!.id,
      to_event_id: event.id,
      relation: group.some((finding) => finding.type === "blocker") ? "blocked" : "informed",
      state: recurrence > 1 ? "captured" : "inferred",
      confidence: Math.min(0.92, Math.max(0.58, event.confidence - 0.04)),
      evidence_refs: event.evidence_refs,
    }));
    const confidence =
      sortedGroup.reduce((total, finding) => total + finding.confidence, 0) / sortedGroup.length;
    const createdAt = events[0]?.timestamp ?? generatedAt;
    const updatedAt = events[events.length - 1]?.timestamp ?? generatedAt;

    return {
      id: trailId,
      kind: trailKindForEntity(entityType),
      title: trailTitleForGroup(entityType, sortedGroup),
      summary: trailSummaryForGroup(sortedGroup),
      subject_entity_id: entityId,
      subject_entity_type: entityType,
      state: trailStateForFindings(sortedGroup),
      valence: trailValenceForFindings(sortedGroup, recurrence),
      confidence: Number(confidence.toFixed(2)),
      recurrence_score: recurrence <= 1 ? Math.min(24, evidenceRefs.length * 8 + 10) : Math.min(100, recurrence * 24 + evidenceRefs.length * 7),
      impact_score: Math.min(100, Math.round(first.confidence * 70) + recurrence * 8 + (index < 4 ? 10 : 0)),
      privacy_state: "redacted",
      events,
      edges,
      evidence_refs: evidenceRefs,
      blocker_ids: sortedGroup.filter((finding) => finding.type === "blocker").map((finding) => finding.evidence_ref),
      recommendation_ids: [],
      created_at: createdAt,
      updated_at: updatedAt,
      shape: trailShapeForFindings(sortedGroup, recurrence),
    };
  });
}

function severityFor(count: number): WorkGraphRecurringPattern["severity"] {
  if (count >= 6) return "critical";
  if (count >= 3) return "high";
  if (count >= 2) return "medium";
  return "low";
}

function trailsForType(trails: readonly WorkGraphTrail[], entityType: WorkGraphEntityType): WorkGraphTrail[] {
  return trails.filter((trail) => trail.subject_entity_type === entityType);
}

export function buildRecurringPatterns(
  coverage: WorkGraphSourceCoverage,
  findings: readonly WorkGraphFinding[],
  trails: readonly WorkGraphTrail[],
): WorkGraphRecurringPattern[] {
  const patterns: WorkGraphRecurringPattern[] = [];
  const decisionTrails = trailsForType(trails, "decision");
  const artifactTrails = trailsForType(trails, "artifact");
  const blockerTrails = trailsForType(trails, "blocker");
  const personTrails = trailsForType(trails, "person");
  const businessTrails = trailsForType(trails, "business");
  const highRecurrence = trails.filter((trail) => trail.recurrence_score >= 56);

  if (decisionTrails.length > 0 && !coverage.orgxMcpCalled) {
    patterns.push({
      id: "pattern:trapped-decision",
      title: "Decisions are being made without durable OrgX writeback",
      description: `${decisionTrails.length} decision finding${decisionTrails.length === 1 ? "" : "s"} appeared while no OrgX MCP write was detected.`,
      pattern_type: "trapped_decision",
      affected_trail_ids: decisionTrails.map((trail) => trail.id),
      affected_entity_ids: decisionTrails.map((trail) => trail.subject_entity_id),
      recurrence_count: decisionTrails.length,
      severity: severityFor(decisionTrails.length + 1),
      confidence: coverage.orgxObserved ? 0.82 : 0.74,
      valence: "leak",
      root_cause_hypothesis: "Agents and humans are making useful choices, but the runtime is not promoting those choices into organizational memory.",
      recommended_runtime_action_id: "recommendation:promote-decisions",
    });
  }

  if (artifactTrails.length > 0 && personTrails.length === 0) {
    patterns.push({
      id: "pattern:orphaned-artifact",
      title: "Artifacts do not have visible ownership",
      description: `${artifactTrails.length} artifact finding${artifactTrails.length === 1 ? "" : "s"} appeared without a clear owner-visible record.`,
      pattern_type: "orphaned_artifact",
      affected_trail_ids: artifactTrails.map((trail) => trail.id),
      affected_entity_ids: artifactTrails.map((trail) => trail.subject_entity_id),
      recurrence_count: artifactTrails.length,
      severity: severityFor(artifactTrails.length),
      confidence: 0.72,
      valence: "risk",
      root_cause_hypothesis: "Work is becoming concrete, but downstream accountability is not being attached at creation time.",
      recommended_runtime_action_id: "recommendation:assign-owner",
    });
  }

  if (coverage.missing.length > 0) {
    const sourceTrails = trailsForType(trails, "source");
    patterns.push({
      id: "pattern:missing-source",
      title: "Important source coverage is missing",
      description: `Missing sources: ${coverage.missing.join(", ")}.`,
      pattern_type: "missing_source",
      affected_trail_ids: sourceTrails.map((trail) => trail.id),
      affected_entity_ids: sourceTrails.map((trail) => trail.subject_entity_id),
      recurrence_count: coverage.missing.length,
      severity: severityFor(coverage.missing.length),
      confidence: 0.78,
      valence: "leak",
      root_cause_hypothesis: "The scan can see work happening, but coordination and proof sources are not fully connected.",
      recommended_runtime_action_id: "recommendation:connect-source",
    });
  }

  if (coverage.mcpObserved && !coverage.orgxMcpCalled) {
    patterns.push({
      id: "pattern:tooling-mismatch",
      title: "MCP was present, but OrgX was not called",
      description: "The session mentions MCP/tooling activity, but no durable OrgX MCP call was detected.",
      pattern_type: "tooling_mismatch",
      affected_trail_ids: trails.map((trail) => trail.id).slice(0, 6),
      affected_entity_ids: trails.map((trail) => trail.subject_entity_id).slice(0, 6),
      recurrence_count: findings.filter((finding) => /\bmcp|tool\b/i.test(finding.summary)).length || 1,
      severity: "high",
      confidence: 0.8,
      valence: "leak",
      root_cause_hypothesis: "The available tool layer is not automatically closing the loop when work finishes.",
      recommended_runtime_action_id: "recommendation:install-runtime-hooks",
    });
  }

  if (highRecurrence.length > 0) {
    patterns.push({
      id: "pattern:repeated-work",
      title: "The same work shape is recurring",
      description: `${highRecurrence.length} finding${highRecurrence.length === 1 ? "" : "s"} repeat strongly enough to deserve durable operating memory.`,
      pattern_type: "repeated_work",
      affected_trail_ids: highRecurrence.map((trail) => trail.id),
      affected_entity_ids: highRecurrence.map((trail) => trail.subject_entity_id),
      recurrence_count: highRecurrence.reduce((total, trail) => total + trail.events.length, 0),
      severity: severityFor(highRecurrence.length),
      confidence: 0.7,
      valence: "useful_recurrence",
      root_cause_hypothesis: "Repeated work is creating a reusable operating pattern, but it is still only implicit.",
      recommended_runtime_action_id: "recommendation:launch-initiative",
    });
  }

  if (businessTrails.length > 0 && findings.every((finding) => finding.type !== "initiative_candidate")) {
    patterns.push({
      id: "pattern:business-signal-unclaimed",
      title: "Business signal has not become a launchable initiative",
      description: `${businessTrails.length} business finding${businessTrails.length === 1 ? "" : "s"} appeared without a matching initiative candidate.`,
      pattern_type: "business_signal_unclaimed",
      affected_trail_ids: businessTrails.map((trail) => trail.id),
      affected_entity_ids: businessTrails.map((trail) => trail.subject_entity_id),
      recurrence_count: businessTrails.length,
      severity: severityFor(businessTrails.length),
      confidence: 0.68,
      valence: "opportunity",
      root_cause_hypothesis: "Revenue or ROI signal exists, but the operating graph has not turned it into an account or initiative loop.",
      recommended_runtime_action_id: "recommendation:launch-initiative",
    });
  }

  if (blockerTrails.length > 0) {
    const topBlockerTrail = [...blockerTrails].sort((left, right) =>
      right.events.length - left.events.length || right.impact_score - left.impact_score
    )[0];
    patterns.push({
      id: "pattern:handoff-friction",
      title: topBlockerTrail && topBlockerTrail.events.length > 1
        ? `${topBlockerTrail.title} and needs owner-visible resolution`
        : "Blockers are becoming handoff friction",
      description: `${blockerTrails.length} blocker finding${blockerTrails.length === 1 ? "" : "s"} need owner-visible resolution.`,
      pattern_type: "handoff_friction",
      affected_trail_ids: blockerTrails.map((trail) => trail.id),
      affected_entity_ids: blockerTrails.map((trail) => trail.subject_entity_id),
      recurrence_count: blockerTrails.length,
      severity: severityFor(blockerTrails.length + 1),
      confidence: 0.76,
      valence: "risk",
      root_cause_hypothesis: "Execution is producing unresolved edges that need to become assigned decisions or tasks.",
      recommended_runtime_action_id: "recommendation:assign-owner",
    });
  }

  return patterns.slice(0, 8);
}

export function buildTrailRecommendations(
  patterns: readonly WorkGraphRecurringPattern[],
  trails: readonly WorkGraphTrail[],
): WorkGraphTrailRecommendation[] {
  const recommendations: WorkGraphTrailRecommendation[] = [];
  const add = (recommendation: WorkGraphTrailRecommendation) => {
    if (!recommendations.some((existing) => existing.id === recommendation.id)) {
      recommendations.push(recommendation);
    }
  };

  for (const pattern of patterns) {
    const evidenceRefs = sortedUnique(
      trails
        .filter((trail) => pattern.affected_trail_ids.includes(trail.id))
        .flatMap((trail) => trail.evidence_refs),
    ).slice(0, 8);
    if (pattern.pattern_type === "trapped_decision") {
      add({
        id: "recommendation:promote-decisions",
        title: "Promote trapped decisions",
        summary: "Turn inferred decisions into reviewed OrgX decision records with owners and downstream artifact links.",
        action_type: "promote_decision",
        trail_ids: pattern.affected_trail_ids,
        evidence_refs: evidenceRefs,
        priority: pattern.severity === "critical" || pattern.severity === "high" ? "p0" : "p1",
        expected_lift: "+decision durability",
        confidence: pattern.confidence,
      });
    } else if (pattern.pattern_type === "orphaned_artifact") {
      add({
        id: "recommendation:assign-owner",
        title: "Assign ownership to orphaned artifacts",
        summary: "Attach owners and next actions to artifacts before they decay into unqueryable receipts.",
        action_type: "assign_owner",
        trail_ids: pattern.affected_trail_ids,
        evidence_refs: evidenceRefs,
        priority: "p1",
        expected_lift: "+owner clarity",
        confidence: pattern.confidence,
      });
    } else if (pattern.pattern_type === "missing_source") {
      add({
        id: "recommendation:connect-source",
        title: "Connect missing source coverage",
        summary: "Close the evidence gap by connecting the coordination or proof sources where findings lose ownership or verification context.",
        action_type: "connect_source",
        trail_ids: pattern.affected_trail_ids,
        evidence_refs: evidenceRefs,
        priority: "p1",
        expected_lift: "+source confidence",
        confidence: pattern.confidence,
      });
    } else if (pattern.pattern_type === "tooling_mismatch") {
      add({
        id: "recommendation:install-runtime-hooks",
        title: "Install runtime writeback hooks",
        summary: "Use post-session reconciliation so useful work becomes OrgX activity even when the agent forgets.",
        action_type: "verify_outcome",
        trail_ids: pattern.affected_trail_ids,
        evidence_refs: evidenceRefs,
        priority: "p0",
        expected_lift: "+continuous attribution",
        confidence: pattern.confidence,
      });
    } else if (pattern.pattern_type === "handoff_friction") {
      add({
        id: "recommendation:resolve-blocker-handoff",
        title: "Resolve the blocker handoff",
        summary: "Promote the recurring blocker path into assigned work with proof requirements and a clear owner.",
        action_type: "assign_owner",
        trail_ids: pattern.affected_trail_ids,
        evidence_refs: evidenceRefs,
        priority: pattern.severity === "critical" || pattern.severity === "high" ? "p0" : "p1",
        expected_lift: "+execution continuity",
        confidence: pattern.confidence,
      });
    } else if (pattern.pattern_type === "business_signal_unclaimed" || pattern.pattern_type === "repeated_work") {
      add({
        id: "recommendation:launch-initiative",
        title: "Launch from this evidence",
        summary: "Convert the highest-recurring evidence path into an OrgX initiative with proof requirements.",
        action_type: "launch_initiative",
        trail_ids: pattern.affected_trail_ids,
        evidence_refs: evidenceRefs,
        priority: pattern.severity === "critical" || pattern.severity === "high" ? "p0" : "p1",
        expected_lift: "+initiative readiness",
        confidence: pattern.confidence,
      });
    }
  }

  if (recommendations.length === 0 && trails.length > 0) {
    const topTrail = [...trails].sort((a, b) => b.impact_score - a.impact_score)[0]!;
    add({
      id: "recommendation:inspect-top-trail",
      title: "Inspect the strongest finding",
      summary: "Review the highest-confidence evidence path and decide whether it should become durable OrgX memory.",
      action_type: "launch_initiative",
      trail_ids: [topTrail.id],
      evidence_refs: topTrail.evidence_refs,
      priority: "p2",
      expected_lift: "+operating memory",
      confidence: topTrail.confidence,
    });
  }

  const priorityRank = { p0: 0, p1: 1, p2: 2 };
  return recommendations
    .sort((left, right) =>
      priorityRank[left.priority] - priorityRank[right.priority] ||
      right.confidence - left.confidence
    )
    .slice(0, 5);
}

export function buildWorkGraphMirror(input: {
  auditMethod?: WorkGraphAuditMethod;
  coverage: WorkGraphSourceCoverage;
  domains?: readonly WorkGraphDomainSignal[];
  generatedAt: string;
  impact?: WorkGraphImpactProjection;
  patterns: readonly WorkGraphRecurringPattern[];
  recommendations: readonly WorkGraphTrailRecommendation[];
  skillToolSignals?: readonly WorkGraphSkillToolSignal[];
  trails: readonly WorkGraphTrail[];
}): WorkGraphMirror {
  const { auditMethod, coverage, domains = [], generatedAt, impact, patterns, recommendations, skillToolSignals = [], trails } = input;
  const topTrail = selectPublicTopTrail(trails);
  const topPattern = [...patterns].sort((a, b) => b.recurrence_count - a.recurrence_count)[0];
  const decisionCount = trailsForType(trails, "decision").length;
  const artifactCount = trailsForType(trails, "artifact").length;
  const blockerCount = trailsForType(trails, "blocker").length;
  const sourceGapCount = coverage.missing.length;
  const sourceClients = sortedUnique(
    trails.flatMap((trail) => trail.events.map((event) => event.source_type)),
  );
  const sourcePhrase = sourceClients.length > 0
    ? sourceClients.map(labelForSourceClient).join(", ")
    : coverage.connected.join(", ") || "local sources";
  const namedSourceGaps = coverage.missing.slice(0, 5);
  const sourceGapPhrase = namedSourceGaps.length > 0
    ? namedSourceGaps.join(", ")
    : "no declared source gaps";
  const headline = blockerCount > 0
    ? "AI-native receipts: your agents are working, but the proof chain is thin"
    : sourceGapCount > 0
      ? "AI-native receipts: your sessions describe a business system, but the proof chain is thin"
      : topPattern
      ? "Your work has a recurring operating pattern worth preserving"
        : topTrail
          ? "Your work is becoming an operating profile"
          : "Your work is leaving evidence OrgX can organize";
  const domainPhrase = domains.length > 0
    ? domains.slice(0, 4).map((domain) => domain.label).join(", ")
    : "the connected work surface";
  const toolPhrase = skillToolSignals.length > 0
    ? skillToolSignals.slice(0, 4).map((signal) => signal.label).join(", ")
    : "client sessions and available tools";
  const primaryClaimRefs = topTrail?.evidence_refs ?? [];
  const claims: WorkGraphMirrorClaim[] = [
    {
      id: "mirror:trail-count",
      text: `${trails.length} evidence-backed finding${trails.length === 1 ? "" : "s"} were detected across ${coverage.connected.length} connected source${coverage.connected.length === 1 ? "" : "s"}.`,
      evidence_refs: trails.flatMap((trail) => trail.evidence_refs).slice(0, 6),
      confidence: trails.length > 0 ? 0.82 : 0.55,
    },
    {
      id: "mirror:decision-artifact-balance",
      text: `${decisionCount} decision finding${decisionCount === 1 ? "" : "s"} and ${artifactCount} artifact finding${artifactCount === 1 ? "" : "s"} were found.`,
      evidence_refs: trails
        .filter((trail) => trail.subject_entity_type === "decision" || trail.subject_entity_type === "artifact")
        .flatMap((trail) => trail.evidence_refs)
        .slice(0, 6),
      confidence: 0.78,
    },
    {
      id: "mirror:missing-sources",
      text: sourceGapCount > 0
        ? `${sourceGapCount} source gap${sourceGapCount === 1 ? "" : "s"} still limit attribution depth.`
        : "The connected sources are enough for a first operating profile, pending human review.",
      evidence_refs: trailsForType(trails, "source").flatMap((trail) => trail.evidence_refs).slice(0, 4),
      confidence: sourceGapCount > 0 ? 0.76 : 0.66,
    },
    ...(impact ? [{
      id: "mirror:impact",
      text: `${impact.time_saved_hours_per_week} hours/week and +${impact.acceleration_percent}% execution acceleration are recoverable if the top findings get owner, proof, and writeback.`,
      evidence_refs: trails.flatMap((trail) => trail.evidence_refs).slice(0, 6),
      confidence: impact.confidence,
    }] : []),
  ];
  const searchClaim = auditMethod
    ? `OrgX searched ${auditMethod.searched_session_files} AI-client session file${auditMethod.searched_session_files === 1 ? "" : "s"}${auditMethod.searched_message_count > 0 ? ` and ${auditMethod.searched_message_count} message turns` : ""} across ${sourcePhrase}; it kept ${auditMethod.retained_evidence_lines} public-safe evidence line${auditMethod.retained_evidence_lines === 1 ? "" : "s"} and left raw transcripts out.`
    : `OrgX searched your AI-client session evidence across ${sourcePhrase} without requiring OrgX tool calls to be present.`;
  const topIssue = topTrail
    ? `The clearest operating leak is "${topTrail.title}".`
    : topPattern
      ? `The clearest operating pattern is "${topPattern.title}".`
      : "The first profile is forming from sparse evidence and should be reviewed before promotion.";
  const proofGapSentence = sourceGapCount > 0
    ? `What keeps the page from feeling decisive is not the amount of activity; it is missing proof from ${sourceGapPhrase}.`
    : "The proof chain is strong enough to publish as a first-pass operating profile, with human review still in the loop.";
  const evidenceShapeSentence = [
    `${trails.length} finding${trails.length === 1 ? "" : "s"}`,
    `${decisionCount} decision${decisionCount === 1 ? "" : "s"}`,
    `${artifactCount} artifact${artifactCount === 1 ? "" : "s"}`,
    blockerCount > 0 ? `${blockerCount} blocker${blockerCount === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(", ");
  const recommendation = recommendations[0];
  const repairSentence = recommendation
    ? `The next repair is ${recommendation.title}: it should turn the strongest evidence path into owner-visible work with linked proof, not another summary.`
    : "The next move is to inspect the highest-confidence finding before publishing it.";
  const body = [
    searchClaim,
    "This is receipts over vibes: the score should rise when more runtime proof connects, not fall because a source is not installed yet.",
    `The shape is specific: ${evidenceShapeSentence} across ${domainPhrase}, with ${toolPhrase} showing up as the strongest skills, tools, or sources.`,
    topIssue,
    blockerCount > 0
      ? `${blockerCount} blocker finding${blockerCount === 1 ? "" : "s"} show work returning as new work instead of becoming owner-visible resolution.`
      : `${decisionCount} decision finding${decisionCount === 1 ? "" : "s"} and ${artifactCount} artifact finding${artifactCount === 1 ? "" : "s"} show where work can become durable.`,
    proofGapSentence,
    impact
      ? `Left unresolved, the profile estimates ${impact.time_saved_hours_per_week} recoverable hours/week and about $${impact.estimated_monthly_value_usd.toLocaleString("en-US")}/month in operator leverage.`
      : "",
    repairSentence,
  ].filter(Boolean).join(" ");

  return {
    headline,
    body,
    lens: "all",
    claims,
    ...(topTrail ? { primary_trail_id: topTrail.id } : {}),
    generated_at: generatedAt,
  };
}

function selectPublicTopTrail(trails: readonly WorkGraphTrail[]): WorkGraphTrail | undefined {
  const ranked = [...trails].sort((left, right) => publicTrailRank(right) - publicTrailRank(left));
  return ranked[0];
}

function publicTrailRank(trail: WorkGraphTrail): number {
  const title = trail.title.trim();
  let score = trail.impact_score;
  if (trail.events.length > 1) score += 10;
  if (trail.subject_entity_type === "blocker") score += 6;
  if (/\b(?:scaffold|ship_batch|mcp__orgx__orgx_search|list entities|schema validation|route behavior|dispatching agent runs|operation qa loop)\b/i.test(title)) {
    score += 14;
  }
  if (/^(?:[-*✓{`'"]|\d+[,.]?$)/.test(title)) score -= 32;
  if (/[{}|;]/.test(title)) score -= 28;
  if (title.length < 22) score -= 26;
  if (/^(?:result|summary)\s*:/i.test(title)) score -= 18;
  return score;
}

export function buildTensionMetrics(input: {
  coverage: WorkGraphSourceCoverage;
  impact?: WorkGraphImpactProjection;
  patterns: readonly WorkGraphRecurringPattern[];
  quality?: WorkGraphExecutionQualityScore;
  trails: readonly WorkGraphTrail[];
}): WorkGraphTensionMetric[] {
  const { coverage, impact, patterns, quality, trails } = input;
  const decisionTrails = trailsForType(trails, "decision");
  const blockerTrails = trailsForType(trails, "blocker");
  const artifactTrails = trailsForType(trails, "artifact");
  const missingSourceTrails = trailsForType(trails, "source");
  const topReady = trails.filter((trail) => trail.impact_score >= 70 && trail.confidence >= 0.75);

  return [
    ...(quality ? [{
      id: "tension:quality-score",
      label: "quality score",
      value: `${quality.overall}/100`,
      tone: quality.overall >= 82 ? "good" as const : quality.overall >= 65 ? "warning" as const : "danger" as const,
      trail_ids: trails.slice(0, 8).map((trail) => trail.id),
      evidence_refs: trails.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "Weighted audit quality across evidence coverage, attribution, chronology depth, insight depth, actionability, and impact confidence.",
    }] : []),
    ...(impact ? [{
      id: "tension:time-saved",
      label: "hours recoverable",
      value: `${impact.time_saved_hours_per_week}h/wk`,
      tone: impact.time_saved_hours_per_week >= 4 ? "warning" as const : "muted" as const,
      trail_ids: patterns.flatMap((pattern) => pattern.affected_trail_ids).slice(0, 8),
      evidence_refs: trails.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "Estimated time recovered by promoting repeated decisions, blockers, artifacts, and writeback gaps into durable operating memory.",
    }, {
      id: "tension:acceleration",
      label: "acceleration",
      value: `+${impact.acceleration_percent}%`,
      tone: impact.acceleration_percent >= 30 ? "good" as const : "muted" as const,
      trail_ids: patterns.flatMap((pattern) => pattern.affected_trail_ids).slice(0, 8),
      evidence_refs: trails.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "Directional execution lift from connecting source evidence, writeback, owners, and action recommendations.",
    }] : []),
    {
      id: "tension:work-leaks",
      label: "work leaks",
      value: String(patterns.filter((pattern) => pattern.valence === "leak" || pattern.valence === "risk").length),
      tone: patterns.some((pattern) => pattern.severity === "critical" || pattern.severity === "high") ? "danger" : "warning",
      trail_ids: patterns.flatMap((pattern) => pattern.affected_trail_ids).slice(0, 8),
      evidence_refs: trails.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "Patterns where evidence exists but ownership, source coverage, or writeback is incomplete.",
    },
    {
      id: "tension:decisions-decaying",
      label: "decisions decaying",
      value: String(decisionTrails.filter((trail) => trail.state === "inferred" || trail.state === "missing_evidence").length),
      tone: decisionTrails.length > 0 && !coverage.orgxMcpCalled ? "danger" : "muted",
      trail_ids: decisionTrails.map((trail) => trail.id),
      evidence_refs: decisionTrails.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "Decision evidence that has not been promoted into durable OrgX records.",
    },
    {
      id: "tension:artifacts-orphaned",
      label: "artifacts orphaned",
      value: String(artifactTrails.filter((trail) => trail.state !== "verified").length),
      tone: artifactTrails.some((trail) => trail.shape === "artifact_heavy_decision_light") ? "warning" : "muted",
      trail_ids: artifactTrails.map((trail) => trail.id),
      evidence_refs: artifactTrails.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "Artifacts that exist without complete decision, verification, or owner context.",
    },
    {
      id: "tension:sources-missing",
      label: "sources missing",
      value: String(coverage.missing.length),
      tone: coverage.missing.length > 0 ? "warning" : "good",
      trail_ids: missingSourceTrails.map((trail) => trail.id),
      evidence_refs: missingSourceTrails.flatMap((trail) => trail.evidence_refs).slice(0, 6),
      explanation: "Disconnected coordination or proof sources that limit attribution confidence.",
    },
    {
      id: "tension:launch-ready",
      label: "launch ready",
      value: String(topReady.length),
      tone: topReady.length > 0 && blockerTrails.length === 0 ? "good" : "muted",
      trail_ids: topReady.map((trail) => trail.id).slice(0, 6),
      evidence_refs: topReady.flatMap((trail) => trail.evidence_refs).slice(0, 8),
      explanation: "High-confidence findings that can become initiatives, decisions, artifacts, or owner-visible follow-ups.",
    },
  ];
}

function countFindingsByType(
  findings: readonly WorkGraphFinding[],
): Partial<Record<WorkGraphFindingType, number>> {
  const counts: Partial<Record<WorkGraphFindingType, number>> = {};
  for (const finding of findings) {
    counts[finding.type] = (counts[finding.type] ?? 0) + 1;
  }
  return Object.fromEntries(
    Object.entries(counts).sort(([left], [right]) => left.localeCompare(right)),
  ) as Partial<Record<WorkGraphFindingType, number>>;
}

function isIsoTimestamp(value: unknown): value is string {
  return typeof value === "string" && !Number.isNaN(Date.parse(value));
}

function redactionStateForFinding(finding: WorkGraphFinding): WorkGraphRedactionState {
  const privacy = typeof finding.metadata.privacy_state === "string"
    ? finding.metadata.privacy_state
    : "";
  if (privacy === "private") return "private";
  if (privacy === "redacted") return "redacted";
  return "public_summary";
}

function attributionKindForFinding(
  type: WorkGraphFindingType,
): WorkGraphAttributionKind | null {
  switch (type) {
    case "action":
    case "blocker":
    case "outcome":
      return "action";
    case "decision":
    case "artifact":
    case "person":
    case "business":
    case "product_surface":
    case "goal":
      return type;
    case "initiative_candidate":
      return "goal";
    case "missed_orchestration_opportunity":
      return "source";
    default:
      return null;
  }
}

function attributionEventTypeForFinding(
  finding: WorkGraphFinding,
): WorkGraphAttributionSourceEvent["event_type"] {
  if (finding.source_client === "slack") return "slack_message";
  if (finding.source_client === "github") return "github_event";
  if (finding.source_client === "linear") return "linear_event";
  if (finding.source_client === "gmail") return "gmail_message";
  if (finding.source_client === "calendar") return "calendar_event";
  if (finding.source_client === "notion" || finding.source_client === "docs") return "doc_signal";
  if (finding.source_client === "mcp") return "mcp_call";
  if (finding.type === "artifact") return "repo_artifact";
  if (finding.type === "missed_orchestration_opportunity") return "source_coverage";
  if (/\b(mcp|tool|hook|runtime)\b/i.test(`${finding.title} ${finding.summary}`)) return "tool_signal";
  return "client_extraction";
}

function nodeBucketForKind(
  kind: WorkGraphAttributionKind,
): keyof Pick<
  WorkGraphAttributionSpine,
  | "actions"
  | "decisions"
  | "artifacts"
  | "people"
  | "agents"
  | "tools"
  | "businesses"
  | "product_surfaces"
  | "goals"
  | "sources"
  | "initiative_candidates"
> {
  switch (kind) {
    case "decision":
      return "decisions";
    case "artifact":
      return "artifacts";
    case "person":
      return "people";
    case "agent":
      return "agents";
    case "tool":
      return "tools";
    case "business":
      return "businesses";
    case "product_surface":
      return "product_surfaces";
    case "goal":
      return "goals";
    case "source":
      return "sources";
    case "action":
    default:
      return "actions";
  }
}

function nodeIdForFinding(kind: WorkGraphAttributionKind, finding: WorkGraphFinding): string {
  const dedupeSource = finding.metadata.dedupe_key ?? finding.evidence_ref ?? finding.title;
  return `${kind}:${slugPart(finding.title)}:${shortHash(dedupeSource, 8)}`;
}

function buildFindingNode(
  finding: WorkGraphFinding,
  kind: WorkGraphAttributionKind,
): WorkGraphAttributionNode {
  const id = nodeIdForFinding(kind, finding);
  return {
    id,
    kind,
    label: finding.title,
    summary: finding.summary,
    source_client: finding.source_client,
    evidence_refs: [finding.evidence_ref],
    linked_node_ids: [],
    confidence: finding.confidence,
    weight: clampScore(finding.confidence * 100),
    review_state: "unreviewed",
    dedupe_key: `${kind}:${slugPart(finding.title)}`,
    metadata: {
      source_id: finding.source_id,
      finding_type: finding.type,
      ...finding.metadata,
    },
  };
}

function mergeNodes(nodes: WorkGraphAttributionNode[]): WorkGraphAttributionNode[] {
  const merged = new Map<string, WorkGraphAttributionNode>();
  for (const node of nodes) {
    const key = node.dedupe_key ?? node.id;
    const previous = merged.get(key);
    if (!previous) {
      merged.set(key, node);
      continue;
    }
    merged.set(key, {
      ...previous,
      confidence: Math.max(previous.confidence, node.confidence),
      evidence_refs: sortedUnique([...previous.evidence_refs, ...node.evidence_refs]),
      linked_node_ids: sortedUnique([...previous.linked_node_ids, ...node.linked_node_ids]),
      weight: Math.max(previous.weight, node.weight),
    });
  }
  return [...merged.values()].sort((left, right) => right.weight - left.weight);
}

function sourceNode(source: string, connected: boolean): WorkGraphAttributionNode {
  const label = source.trim() || "Unknown source";
  return {
    id: `source:${slugPart(label)}:${connected ? "connected" : "missing"}`,
    kind: "source",
    label,
    summary: connected
      ? `${label} contributed evidence to this Work Graph.`
      : `${label} was not connected, so related attribution remains incomplete.`,
    evidence_refs: [],
    linked_node_ids: [],
    confidence: connected ? 0.8 : 0.52,
    weight: connected ? 70 : 62,
    review_state: connected ? "unreviewed" : "important",
    dedupe_key: `source:${slugPart(label)}`,
    metadata: { connected },
  };
}

function buildToolNodes(coverage: WorkGraphSourceCoverage, findings: readonly WorkGraphFinding[]): WorkGraphAttributionNode[] {
  const mentionsOrgxTool =
    coverage.mcpObserved ||
    findings.some((finding) => /\b(orgx[_-]?mcp|orgx_emit|mcp__orgx|tool call)\b/i.test(`${finding.title} ${finding.summary}`));
  if (!mentionsOrgxTool) return [];
  return [
    {
      id: "tool:orgx-mcp",
      kind: "tool",
      label: "OrgX MCP",
      summary: coverage.orgxMcpCalled
        ? "OrgX MCP was observed as a durable writeback surface in the audited work."
        : "OrgX MCP was available or mentioned, but the audit found incomplete durable writeback.",
      source_client: "mcp",
      evidence_refs: findings
        .filter((finding) => /\b(orgx|mcp|tool)\b/i.test(`${finding.title} ${finding.summary}`))
        .map((finding) => finding.evidence_ref)
        .slice(0, 12),
      linked_node_ids: [],
      confidence: coverage.orgxMcpCalled ? 0.86 : 0.72,
      weight: coverage.orgxMcpCalled ? 88 : 76,
      review_state: "unreviewed",
      dedupe_key: "tool:orgx-mcp",
      metadata: {
        mcp_observed: coverage.mcpObserved,
        orgx_observed: coverage.orgxObserved,
        orgx_mcp_called: coverage.orgxMcpCalled,
        skill_only_signal: coverage.skillOnlySignal,
      },
    },
  ];
}

function buildAgentNodes(findings: readonly WorkGraphFinding[]): WorkGraphAttributionNode[] {
  const actorIds = sortedUnique(
    findings
      .map((finding) => typeof finding.metadata.actor_id === "string" ? finding.metadata.actor_id.trim() : "")
      .filter(Boolean),
  );
  const sourceAgents = sortedUnique(
    findings
      .map((finding) =>
        ["codex", "claude", "claude-code", "cursor", "opencode", "goose", "openclaw"].includes(finding.source_client)
          ? finding.source_client
          : "",
      )
      .filter(Boolean) as WorkGraphSourceClient[],
  );
  return [...actorIds, ...sourceAgents].slice(0, 20).map((agent) => ({
    id: `agent:${slugPart(agent)}`,
    kind: "agent",
    label: agent,
    summary: `${agent} contributed evidence to the Work Graph audit.`,
    source_client: sourceAgents.includes(agent as WorkGraphSourceClient)
      ? agent as WorkGraphSourceClient
      : "manual",
    evidence_refs: findings
      .filter((finding) => finding.metadata.actor_id === agent || finding.source_client === agent)
      .map((finding) => finding.evidence_ref)
      .slice(0, 12),
    linked_node_ids: [],
    confidence: 0.72,
    weight: 64,
    review_state: "unreviewed",
    dedupe_key: `agent:${slugPart(agent)}`,
    metadata: {},
  }));
}

export function buildWorkGraphAttributionSpine(input: {
  coverage: WorkGraphSourceCoverage;
  events: readonly WorkGraphSourceEvent[];
  findings: readonly WorkGraphFinding[];
}): WorkGraphAttributionSpine {
  const evidence_refs: WorkGraphEvidenceRef[] = input.findings.map((finding) => ({
    id: finding.evidence_ref,
    source_client: finding.source_client,
    source_id: finding.source_id,
    label: finding.title,
    summary: finding.summary.slice(0, 1200),
    ...(isIsoTimestamp(finding.metadata.occurred_at) ? { occurred_at: finding.metadata.occurred_at } : {}),
    confidence: finding.confidence,
    redaction_state: redactionStateForFinding(finding),
    metadata: {
      raw_transcript_sent: false,
      source_label: finding.metadata.source_label ?? null,
    },
  }));

  const source_events: WorkGraphAttributionSourceEvent[] = [
    ...input.events.map((event) => ({
      source_client: event.source_client,
      source_id: event.source_id,
      source_label: event.source_label,
      event_type: event.event_type === "client_extraction"
        ? "client_extraction" as const
        : event.event_type,
      evidence_ref: event.evidence_ref,
      confidence: 0.7,
      metadata: {
        ...event.metadata,
        raw_transcript_sent: false,
      },
    })),
    ...input.findings.map((finding) => ({
      source_client: finding.source_client,
      source_id: finding.source_id,
      source_label:
        typeof finding.metadata.source_label === "string"
          ? finding.metadata.source_label
          : finding.source_id,
      event_type: attributionEventTypeForFinding(finding),
      ...(isIsoTimestamp(finding.metadata.occurred_at) ? { occurred_at: finding.metadata.occurred_at } : {}),
      evidence_ref: finding.evidence_ref,
      ...(typeof finding.metadata.actor_id === "string" ? { actor_ref: finding.metadata.actor_id } : {}),
      confidence: finding.confidence,
      metadata: {
        finding_type: finding.type,
        raw_transcript_sent: false,
      },
    })),
  ];

  const buckets = {
    actions: [] as WorkGraphAttributionNode[],
    decisions: [] as WorkGraphAttributionNode[],
    artifacts: [] as WorkGraphAttributionNode[],
    people: [] as WorkGraphAttributionNode[],
    agents: buildAgentNodes(input.findings),
    tools: buildToolNodes(input.coverage, input.findings),
    businesses: [] as WorkGraphAttributionNode[],
    product_surfaces: [] as WorkGraphAttributionNode[],
    goals: [] as WorkGraphAttributionNode[],
    sources: [
      ...input.coverage.connected.map((source) => sourceNode(source, true)),
      ...input.coverage.missing.map((source) => sourceNode(source, false)),
    ],
    initiative_candidates: [] as WorkGraphAttributionNode[],
  };

  for (const finding of input.findings) {
    const kind = attributionKindForFinding(finding.type);
    if (!kind) continue;
    const node = buildFindingNode(finding, kind);
    const bucket = finding.type === "initiative_candidate"
      ? "initiative_candidates"
      : nodeBucketForKind(kind);
    buckets[bucket].push(node);
  }

  const normalized = {
    source_events: source_events.filter((event, index, all) =>
      all.findIndex((candidate) =>
        [
          candidate.source_client,
          candidate.source_id,
          candidate.event_type,
          candidate.evidence_ref,
        ].join(":") === [
          event.source_client,
          event.source_id,
          event.event_type,
          event.evidence_ref,
        ].join(":")
      ) === index
    ).slice(0, 1000),
    actions: mergeNodes(buckets.actions).slice(0, 500),
    decisions: mergeNodes(buckets.decisions).slice(0, 500),
    artifacts: mergeNodes(buckets.artifacts).slice(0, 500),
    people: mergeNodes(buckets.people).slice(0, 500),
    agents: mergeNodes(buckets.agents).slice(0, 200),
    tools: mergeNodes(buckets.tools).slice(0, 300),
    businesses: mergeNodes(buckets.businesses).slice(0, 200),
    product_surfaces: mergeNodes(buckets.product_surfaces).slice(0, 500),
    goals: mergeNodes(buckets.goals).slice(0, 500),
    sources: mergeNodes(buckets.sources).slice(0, 300),
    initiative_candidates: mergeNodes(buckets.initiative_candidates).slice(0, 50),
    evidence_refs: mergeByEvidenceId(evidence_refs).slice(0, 1000),
  };

  const nodeCount =
    normalized.actions.length +
    normalized.decisions.length +
    normalized.artifacts.length +
    normalized.people.length +
    normalized.agents.length +
    normalized.tools.length +
    normalized.businesses.length +
    normalized.product_surfaces.length +
    normalized.goals.length +
    normalized.sources.length +
    normalized.initiative_candidates.length;
  const confidenceInputs = [
    ...normalized.evidence_refs.map((evidence) => evidence.confidence),
    ...normalized.source_events.map((event) => event.confidence),
  ];
  const confidence = confidenceInputs.length
    ? Number((confidenceInputs.reduce((total, value) => total + value, 0) / confidenceInputs.length).toFixed(2))
    : 0;

  return {
    ...normalized,
    confidence,
    dedupe_keys: sortedUnique([
      ...Object.values(normalized)
        .flat()
        .map((item) => typeof item === "object" && item && "dedupe_key" in item ? String(item.dedupe_key ?? "") : "")
        .filter(Boolean),
    ]).slice(0, 1000),
    privacy: {
      redaction_state: "public_summary",
      raw_transcripts_included: false,
      public_summary_only: true,
    },
    review: {
      pending_count: nodeCount,
      correction_affordances: [
        "confirm",
        "merge",
        "hide",
        "mark_important",
        "launch_or_dismiss",
      ],
    },
  };
}

function mergeByEvidenceId(evidenceRefs: WorkGraphEvidenceRef[]): WorkGraphEvidenceRef[] {
  const merged = new Map<string, WorkGraphEvidenceRef>();
  for (const evidence of evidenceRefs) {
    const previous = merged.get(evidence.id);
    if (!previous || evidence.confidence > previous.confidence) {
      merged.set(evidence.id, evidence);
    }
  }
  return [...merged.values()].sort((left, right) => right.confidence - left.confidence);
}

export function buildWorkGraphFingerprint(input: {
  connectedSources: readonly string[];
  findings: readonly WorkGraphFinding[];
  kickoffs: readonly WorkGraphKickoffCandidate[];
  missingSources: readonly string[];
  recurringPatterns: readonly WorkGraphRecurringPattern[];
  trails: readonly WorkGraphTrail[];
  workspace: WorkGraphWorkspaceRef;
}): { fingerprint: string; basis: WorkGraphFingerprintBasis; hydration: WorkGraphSignupHydration } {
  const sourceClients = sortedUnique(input.findings.map((finding) => finding.source_client));
  const patternHashes = input.findings
    .map((finding) =>
      shortHash({
        type: finding.type,
        title: normalizeFingerprintText(finding.title),
        summary: normalizeFingerprintText(finding.summary),
        source_client: finding.source_client,
      }),
    )
    .sort();
  const kickoffHashes = input.kickoffs
    .map((kickoff) =>
      shortHash({
        title: normalizeFingerprintText(kickoff.title),
        summary: normalizeFingerprintText(kickoff.summary),
        priority: kickoff.priority,
      }),
    )
    .sort();
  const trailShapeHashes = input.trails
    .map((trail) =>
      shortHash({
        kind: trail.kind,
        subject_entity_type: trail.subject_entity_type,
        state: trail.state,
        valence: trail.valence,
        shape: trail.shape,
        title: normalizeFingerprintText(trail.title),
      }),
    )
    .sort();
  const recurringPatternHashes = input.recurringPatterns
    .map((pattern) =>
      shortHash({
        pattern_type: pattern.pattern_type,
        severity: pattern.severity,
        title: normalizeFingerprintText(pattern.title),
        recurrence_count: pattern.recurrence_count,
      }),
    )
    .sort();

  const basis: WorkGraphFingerprintBasis = {
    schema_version: WORK_GRAPH_SCHEMA_VERSION,
    fingerprint_version: WORK_GRAPH_FINGERPRINT_VERSION,
    workspace_hash: shortHash({
      id: normalizeFingerprintText(input.workspace.id),
      name: normalizeFingerprintText(input.workspace.name),
    }),
    source_clients: sourceClients,
    connected_source_hashes: sortedUnique(
      input.connectedSources.map((source) => shortHash(normalizeFingerprintText(source))),
    ),
    missing_source_hashes: sortedUnique(
      input.missingSources.map((source) => shortHash(normalizeFingerprintText(source))),
    ),
    finding_type_counts: countFindingsByType(input.findings),
    pattern_hashes: patternHashes,
    trail_shape_hashes: trailShapeHashes,
    recurring_pattern_hashes: recurringPatternHashes,
    kickoff_hashes: kickoffHashes,
    raw_transcripts_included: false,
  };
  const fingerprint = `wgf_${hashJson(basis).slice(0, 24)}`;

  return {
    fingerprint,
    basis,
    hydration: {
      strategy: "work_graph_fingerprint_claim",
      hydration_key: `orgx:work-graph:${fingerprint}`,
      eligible: input.findings.length > 0,
      notes: [
        "Use this fingerprint to claim the pre-signup Work Graph after account creation.",
        "The fingerprint is derived from normalized patterns, source coverage, and kickoff shapes, not raw transcripts.",
      ],
    },
  };
}

export function buildSessionReconciliationReport(
  input: BuildSessionReconciliationReportInput,
): SessionReconciliationReport {
  const clientExtractions = input.clientExtractions ?? [];
  if (input.imports.length === 0 && clientExtractions.length === 0) {
    throw new Error("At least one source import or AI-client extraction is required to build a Work Graph report.");
  }

  const generatedAt = input.generatedAt ?? new Date().toISOString();
  const extractionProtocol = buildWorkGraphExtractionProtocol();
  const clientExtractionSummaries = summarizeClientExtractions(clientExtractions);
  const connectedSources = sortedUnique([
    ...(input.connectedSources ?? input.imports.map((source) => source.sourceLabel)),
    ...clientExtractionSummaries.map((summary) => summary.source_label),
  ]);
  const missingSources = input.missingSources ?? [];
  const coverage = buildCoverage(input.imports, connectedSources, missingSources, clientExtractions);
  const events = [...buildWorkGraphEvents(input.imports), ...buildClientExtractionEvents(clientExtractions)];
  const findings = [...buildWorkGraphFindings(input.imports), ...buildClientExtractionFindings(clientExtractions)];
  const autoMissed = buildMissedOpportunities(coverage, findings);
  const extractedMissed = findings.filter((finding) => finding.type === "missed_orchestration_opportunity");
  const missed = [...extractedMissed, ...autoMissed].filter((finding, index, all) =>
    all.findIndex((candidate) => candidate.evidence_ref === finding.evidence_ref) === index
  );
  const allFindings = [...findings, ...autoMissed];
  const auditMethod = buildAuditMethod({
    clientExtractions,
    extractionProtocol,
    imports: input.imports,
  });
  const domainCoverage = buildDomainCoverage(allFindings);
  const skillToolSignals = buildSkillToolSignals(allFindings);
  const trails = buildWorkGraphTrails(allFindings, generatedAt);
  const recurringPatterns = buildRecurringPatterns(coverage, allFindings, trails);
  const opportunityScore = scoreOpportunity(coverage, allFindings);
  const initiativeKickoffs = buildKickoffs(allFindings, missed, opportunityScore);
  const recommendations = buildTrailRecommendations(recurringPatterns, trails);
  const impactProjection = estimateImpactProjection({
    coverage,
    findings: allFindings,
    opportunityScore,
    patterns: recurringPatterns,
    trails,
  });
  const executionQuality = scoreExecutionQuality({
    coverage,
    findings: allFindings,
    impact: impactProjection,
    patterns: recurringPatterns,
    recommendations,
    trails,
  });
  const mirror = buildWorkGraphMirror({
    auditMethod,
    coverage,
    domains: domainCoverage,
    generatedAt,
    impact: impactProjection,
    patterns: recurringPatterns,
    recommendations,
    skillToolSignals,
    trails,
  });
  const pageQuality = scorePageQuality({
    auditMethod,
    coverage,
    impact: impactProjection,
    mirror,
    recommendations,
    trails,
  });
  const agenticQuotient = buildAgenticQuotient({
    auditMethod,
    coverage,
    executionQuality,
    findings: allFindings,
    recommendations,
    trails,
  });
  const tensionMetrics = buildTensionMetrics({
    coverage,
    impact: impactProjection,
    patterns: recurringPatterns,
    quality: executionQuality,
    trails,
  });
  const attributionSpine = buildWorkGraphAttributionSpine({
    coverage,
    events,
    findings: allFindings,
  });
  const fingerprint = buildWorkGraphFingerprint({
    connectedSources,
    findings: allFindings,
    kickoffs: initiativeKickoffs,
    missingSources,
    recurringPatterns,
    trails,
    workspace: input.workspace,
  });
  const reportSeed = {
    generatedAt,
    clientExtractions: clientExtractions.map((extraction, index) => ({
      extractionId: normalizeClientExtractionId(extraction, index),
      findingHash: hashJson(extraction.findings),
    })),
    imports: input.imports.map((source) => ({
      sourceId: source.sourceId,
      textHash: hashJson(source.text),
    })),
    workspace: input.workspace,
  };
  const reportHash = hashJson(reportSeed);
  const sessionId = input.sessionId ?? `work-graph-${reportHash.slice(0, 16)}`;
  const investigation = buildWorkGraphInvestigation({
    auditMethod,
    clientExtractions: clientExtractionSummaries,
    coverage,
    events,
    findings: allFindings,
    fingerprint: fingerprint.fingerprint,
    generatedAt,
    impact: impactProjection,
    patterns: recurringPatterns,
    ...(input.investigationRawEvents ? { rawEvents: input.investigationRawEvents } : {}),
    recommendations,
    trails,
  });

  return {
    schema_version: WORK_GRAPH_SCHEMA_VERSION,
    report_id: reportHash.slice(0, 24),
    idempotency_key: `work-graph:${sessionId}:${reportHash.slice(0, 16)}`,
    work_graph_fingerprint: fingerprint.fingerprint,
    fingerprint_version: WORK_GRAPH_FINGERPRINT_VERSION,
    fingerprint_basis: fingerprint.basis,
    signup_hydration: fingerprint.hydration,
    generated_at: generatedAt,
    source_client: "wizard",
    session_id: sessionId,
    workspace: input.workspace,
    extraction_protocol: extractionProtocol,
    audit_method: auditMethod,
    client_extractions: clientExtractionSummaries,
    domain_coverage: domainCoverage,
    skill_tool_signals: skillToolSignals,
    source_coverage: coverage,
    final_state: inferFinalState(allFindings),
    events,
    findings: allFindings,
    missed_orchestration_opportunities: missed,
    trails,
    recurring_patterns: recurringPatterns,
    recommendations,
    mirror,
    tension_metrics: tensionMetrics,
    attribution_spine: attributionSpine,
    opportunity_score: opportunityScore,
    execution_quality: executionQuality,
    page_quality: pageQuality,
    agentic_quotient: agenticQuotient,
    impact_projection: impactProjection,
    investigation,
    initiative_kickoffs: initiativeKickoffs,
    redaction_level: "summary_only",
    raw_transcripts_sent: false,
  };
}

export function renderWorkGraphExtractionProtocolMarkdown(
  protocol: WorkGraphExtractionProtocol = buildWorkGraphExtractionProtocol(),
): string {
  const lines: string[] = [];
  lines.push("# OrgX Work Graph AI-Client Extraction Schema");
  lines.push("");
  lines.push(`Schema version: ${protocol.schema_version}`);
  lines.push(`Mode: ${protocol.mode}`);
  lines.push("");
  lines.push("## Prompt");
  lines.push("");
  lines.push(protocol.prompt);
  lines.push("");
  lines.push("## Objective");
  lines.push("");
  lines.push(protocol.schema.objective);
  lines.push("");
  lines.push("## Search Lenses");
  lines.push("");
  for (const lens of protocol.schema.source_search_strategy) {
    lines.push(`- ${lens.lens}: ${lens.query} Return when: ${lens.return_when}`);
  }
  lines.push("");
  lines.push("## Quality Bar");
  lines.push("");
  for (const item of protocol.schema.quality_bar) {
    lines.push(`- ${item}`);
  }
  lines.push("");
  lines.push("## Reject As Noise");
  lines.push("");
  for (const item of protocol.schema.reject_as_noise) {
    lines.push(`- ${item}`);
  }
  lines.push("");
  lines.push("## Required JSON Shape");
  lines.push("");
  lines.push("```json");
  lines.push(JSON.stringify(protocol.schema.required_output, null, 2));
  lines.push("```");

  return lines.join("\n");
}

export interface RenderWorkGraphMarkdownOptions {
  publicUrl?: string;
  reviewUrl?: string;
}

function markdownText(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (!trimmed || /^undefined$/i.test(trimmed) || /^null$/i.test(trimmed)) return fallback;
  return trimmed;
}

function markdownNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function markdownStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

function markdownSources(value: unknown): string {
  return markdownStrings(value).map((source) => labelForSourceClient(normalizeSourceClient(source))).join(", ") || "source evidence";
}

function markdownPublicTitle(title: unknown, fallback: string): string {
  const source = markdownText(title, fallback);
  return titleFromText(source, fallback);
}

function markdownPublicTrails(report: SessionReconciliationReport): WorkGraphTrail[] {
  const primary = report.mirror.primary_trail_id
    ? report.trails.find((trail) => trail.id === report.mirror.primary_trail_id)
    : selectPublicTopTrail(report.trails);
  const ranked = [...report.trails].sort((left, right) => publicTrailRank(right) - publicTrailRank(left));
  return primary ? [primary, ...ranked.filter((trail) => trail.id !== primary.id)] : ranked;
}

function renderWorkGraphShareables(
  report: SessionReconciliationReport,
  options: RenderWorkGraphMarkdownOptions,
): string[] {
  const lines: string[] = [];
  lines.push("## Shareables");
  lines.push("");
  if (options.publicUrl) {
    lines.push(`Profile URL: ${options.publicUrl}`);
    const separator = options.publicUrl.includes("?") ? "&" : "?";
    lines.push(`Headline card: ${options.publicUrl}/opengraph-image?v=headline`);
    lines.push(`Stack card: ${options.publicUrl}/opengraph-image?v=stack`);
    lines.push(`Receipt card: ${options.publicUrl}/opengraph-image?v=receipt`);
    lines.push(`Gap card: ${options.publicUrl}/opengraph-image?v=gap`);
    lines.push(`Weekly wrapped card: ${options.publicUrl}/opengraph-image?v=wrapped`);
    lines.push(`Tracked share URL: ${options.publicUrl}${separator}ref=share&v=headline`);
    if (options.reviewUrl) lines.push(`Review URL: ${options.reviewUrl}`);
  } else {
    lines.push("- Publish with --public-share to create profile and share-card URLs.");
  }
  lines.push("");
  const aq = report.agentic_quotient;
  const topQuest = aq.repair_quests[0];
  const strongestTrail = markdownPublicTrails(report)[0];
  const firstMove = topQuest
    ? `${topQuest.title} (+${topQuest.expected_aq_lift} AQ): ${topQuest.reason}`
    : "Claim the profile and turn the strongest evidence path into owner-visible work with linked proof.";
  lines.push("Suggested share copy:");
  lines.push(`- AQ ${aq.aq}. Stack ${aq.stack_score}. Durable ${aq.durability_score}. Gap ${aq.agentic_gap}. ${aq.archetype.label}. Receipts attached.`);
  lines.push(`- Just ran my AQ. ${topQuest ? `Next repair: ${topQuest.title} (+${topQuest.expected_aq_lift} AQ).` : "The gap is the game."}`);
  lines.push(`- Receipts > vibes. AQ ${aq.aq} with ${report.impact_projection.time_saved_hours_per_week}h/week recoverable.`);
  lines.push("");
  lines.push("First executable move:");
  lines.push(`- ${firstMove}`);
  if (strongestTrail) {
    lines.push(`- Evidence path: ${markdownPublicTitle(strongestTrail.title, "Top work loop")}`);
  }
  lines.push("");
  return lines;
}

export function renderWorkGraphMarkdown(
  report: SessionReconciliationReport,
  options: RenderWorkGraphMarkdownOptions = {},
): string {
  const lines: string[] = [];
  lines.push("# OrgX Investigation Engine");
  lines.push("");
  lines.push(`Generated: ${report.generated_at}`);
  lines.push(`Workspace: ${report.workspace.name} (${report.workspace.id})`);
  lines.push(`Report ID: ${report.report_id}`);
  lines.push(`Work graph fingerprint: ${report.work_graph_fingerprint}`);
  lines.push(`Hydration key: ${report.signup_hydration.hydration_key}`);
  lines.push(`Final state: ${report.final_state}`);
  lines.push("");
  lines.push(...renderWorkGraphShareables(report, options));
  lines.push("## AI-Client Search Protocol");
  lines.push("");
  lines.push(`Schema: ${report.extraction_protocol.schema_version}`);
  lines.push(report.extraction_protocol.prompt);
  lines.push("");
  lines.push("Search lenses:");
  for (const lens of report.extraction_protocol.schema.source_search_strategy) {
    lines.push(`- ${lens.lens}: ${lens.return_when}`);
  }
  lines.push("");
  lines.push("## Audit Method");
  lines.push("");
  lines.push(`Mode: ${report.audit_method.mode}`);
  lines.push(`Session files searched: ${report.audit_method.searched_session_files}`);
  lines.push(`Message turns searched: ${report.audit_method.searched_message_count}`);
  lines.push(`Evidence lines retained: ${report.audit_method.retained_evidence_lines}`);
  lines.push(`Source groups searched: ${report.audit_method.searched_source_groups}`);
  lines.push(`Extraction lenses: ${report.audit_method.extraction_lenses.join(", ")}`);
  for (const note of report.audit_method.notes) {
    lines.push(`- ${note}`);
  }
  lines.push("");
  lines.push("## Investigation v2");
  lines.push("");
  lines.push(`Schema: ${report.investigation.schema_version}`);
  lines.push(`Audit ID: ${report.investigation.audit_id}`);
  lines.push(`Raw events normalized: ${report.investigation.raw_events_summary.count}`);
  lines.push(`Work loops: ${report.investigation.work_loops.length}`);
  lines.push(`Recurring loop families: ${report.investigation.loop_families.filter((family) => family.public_surface).length}`);
  const passedVerification = report.investigation.verification_log.filter((entry) => entry.passed).length;
  const droppedVerification = report.investigation.verification_log.length - passedVerification;
  lines.push(`Verification: ${passedVerification} passed, ${droppedVerification} dropped as unverifiable`);
  lines.push(`Earned counterfactuals: ${report.investigation.counterfactuals.length}`);
  lines.push("");
  lines.push("Mirror:");
  lines.push(markdownText(report.investigation.mirror_paragraph.text, report.mirror.body));
  lines.push("");
  if (report.investigation.counterfactuals[0]) {
    const counterfactual = report.investigation.counterfactuals[0];
    lines.push("Top earned counterfactual:");
    lines.push(`- Trigger: ${counterfactual.triggered_by_event_id} at ${counterfactual.triggered_at}`);
    lines.push(`- Actual: ${counterfactual.actual_outcome}`);
    lines.push(`- OrgX would call: ${counterfactual.orgx_capability.tool}`);
    lines.push(`- Outcome: ${counterfactual.orgx_outcome}`);
    lines.push("");
  }
  if (report.investigation.why_not_100.length > 0) {
    lines.push("What improves it:");
    for (const gap of report.investigation.why_not_100) {
      lines.push(`- ${gap.precise_gap} ${gap.user_action ? `Action: ${gap.user_action.copy_for_button} (${gap.user_action.ux_route}).` : ""}`);
    }
    lines.push("");
  }
  lines.push("Source capability matrix:");
  for (const source of report.investigation.source_confidence.columns) {
    const chronology = source.capability_cells.chronology;
    const verification = source.capability_cells.verification;
    lines.push(`- ${source.brand.name}: ${source.status}, chronology ${Math.round(chronology.achieved * 100)}%/${chronology.ceiling}, verification ${Math.round(verification.achieved * 100)}%/${verification.ceiling}`);
  }
  lines.push("");
  lines.push("## Client Extractions");
  lines.push("");
  if (report.client_extractions.length === 0) {
    lines.push("- No structured AI-client extraction files were provided; fallback local session import was used.");
  } else {
    for (const extraction of report.client_extractions) {
      lines.push(`- ${extraction.source_label}: ${extraction.finding_count} findings, ${extraction.query_count} queries, ${extraction.searched_source_count} source groups, confidence ${extraction.confidence}`);
    }
  }
  lines.push("");
  lines.push("## Domain Coverage");
  lines.push("");
  if (report.domain_coverage.length === 0) {
    lines.push("- No domain clusters reached the public-summary threshold.");
  } else {
    for (const domain of report.domain_coverage) {
      lines.push(`- ${markdownText(domain.label, "Work evidence")}: ${markdownNumber(domain.finding_count)} findings across ${markdownSources(domain.source_clients)}. ${markdownText(domain.summary, "Evidence cluster retained for public review.")}`);
    }
  }
  lines.push("");
  lines.push("## Skills, Agents, Tools, and Sources");
  lines.push("");
  if (report.skill_tool_signals.length === 0) {
    lines.push("- No repeated skill/tool signals reached the public-summary threshold.");
  } else {
    for (const signal of report.skill_tool_signals) {
      const mentionCount = markdownNumber(signal.mention_count);
      lines.push(`- [${markdownText(signal.kind, "source")}] ${markdownText(signal.label, "Work source")}: ${mentionCount} mention${mentionCount === 1 ? "" : "s"} across ${markdownSources(signal.source_clients)}.`);
    }
  }
  lines.push("");
  lines.push("## Opportunity Score");
  lines.push("");
  lines.push(`Overall: ${report.opportunity_score.overall}/100`);
  lines.push(`Value potential: ${report.opportunity_score.value_potential}/100`);
  lines.push(`Evidence quality: ${report.opportunity_score.evidence_quality}/100`);
  lines.push(`Urgency: ${report.opportunity_score.urgency}/100`);
  lines.push(`Owner clarity: ${report.opportunity_score.owner_clarity}/100`);
  lines.push(`Automation potential: ${report.opportunity_score.automation_potential}/100`);
  lines.push(`OrgX fit: ${report.opportunity_score.orgx_fit}/100`);
  lines.push("");
  lines.push("## Agentic Quotient");
  lines.push("");
  lines.push(`AQ: ${report.agentic_quotient.aq}/100`);
  lines.push(`Stack score: ${report.agentic_quotient.stack_score}/100`);
  lines.push(`Durability score: ${report.agentic_quotient.durability_score}/100`);
  lines.push(`Agentic gap: ${report.agentic_quotient.agentic_gap}`);
  lines.push(`Ceiling: ${report.agentic_quotient.ceiling}/100`);
  lines.push(`Archetype: ${report.agentic_quotient.archetype.label}`);
  lines.push(report.agentic_quotient.archetype.roast);
  lines.push(report.agentic_quotient.archetype.truth);
  lines.push("");
  if (report.agentic_quotient.repair_quests[0]) {
    const quest = report.agentic_quotient.repair_quests[0];
    const primaryTrail = markdownPublicTrails(report)[0];
    lines.push("First executable move:");
    lines.push(`- ${quest.title} (+${quest.expected_aq_lift} AQ): ${quest.reason}`);
    if (primaryTrail) {
      lines.push(`- Starts from: ${markdownPublicTitle(primaryTrail.title, "Top work loop")}`);
    }
    lines.push(`- Why it matters: moves AQ ${report.agentic_quotient.aq} toward ${report.agentic_quotient.ceiling} by closing proof, source, or writeback gaps.`);
    lines.push("");
  }
  if (report.agentic_quotient.repair_quests.length > 0) {
    lines.push("Repair quests:");
    for (const quest of report.agentic_quotient.repair_quests) {
      lines.push(`- +${quest.expected_aq_lift} AQ ${quest.title}: ${quest.reason}`);
    }
    lines.push("");
  }
  for (const note of report.agentic_quotient.notes) {
    lines.push(`- ${note}`);
  }
  lines.push("");
  lines.push("## Execution Quality");
  lines.push("");
  lines.push(`Overall: ${report.execution_quality.overall}/100`);
  lines.push(`Evidence coverage: ${report.execution_quality.evidence_coverage}/100`);
  lines.push(`Source attribution: ${report.execution_quality.source_attribution}/100`);
  lines.push(`Chronology depth: ${report.execution_quality.trail_depth}/100`);
  lines.push(`Insight depth: ${report.execution_quality.insight_depth}/100`);
  lines.push(`Actionability: ${report.execution_quality.actionability}/100`);
  lines.push(`Impact confidence: ${report.execution_quality.impact_confidence}/100`);
  for (const note of report.execution_quality.notes) {
    lines.push(`- ${note}`);
  }
  lines.push("");
  lines.push("## Page Quality");
  lines.push("");
  lines.push(`Overall: ${report.page_quality.overall}/100`);
  lines.push(`Clarity: ${report.page_quality.clarity}/100`);
  lines.push(`Impact: ${report.page_quality.impact}/100`);
  lines.push(`Resonance: ${report.page_quality.resonance}/100`);
  lines.push(`Trust: ${report.page_quality.trust}/100`);
  lines.push(`Repair readiness: ${report.page_quality.repair_readiness}/100`);
  for (const note of report.page_quality.notes) {
    lines.push(`- ${note}`);
  }
  lines.push("");
  lines.push("## Impact Projection");
  lines.push("");
  lines.push(`Time saved: ${report.impact_projection.time_saved_hours_per_week}h/week`);
  lines.push(`Acceleration: +${report.impact_projection.acceleration_percent}%`);
  lines.push(`Estimated monthly value: $${report.impact_projection.estimated_monthly_value_usd.toLocaleString("en-US")}`);
  lines.push(`Confidence: ${report.impact_projection.confidence}`);
  for (const item of report.impact_projection.basis) {
    lines.push(`- ${item}`);
  }
  lines.push("");
  lines.push("## Source Coverage");
  lines.push("");
  lines.push(`Connected: ${report.source_coverage.connected.join(", ") || "none"}`);
  lines.push(`Missing: ${report.source_coverage.missing.join(", ") || "none"}`);
  lines.push(`Coverage score: ${report.source_coverage.coverage_score ?? 0}/100`);
  lines.push(`MCP observed: ${report.source_coverage.mcpObserved ? "yes" : "no"}`);
  lines.push(`OrgX observed: ${report.source_coverage.orgxObserved ? "yes" : "no"}`);
  lines.push(`OrgX MCP called: ${report.source_coverage.orgxMcpCalled ? "yes" : "no"}`);
  if (report.source_coverage.manifests?.length) {
    lines.push("");
    lines.push("Source manifests:");
    for (const manifest of report.source_coverage.manifests) {
      lines.push(`- [${markdownText(manifest.status, "observed")}] ${markdownText(manifest.source_label, "Work source")}: ${markdownNumber(manifest.finding_count)} findings, ${markdownNumber(manifest.searched_session_count)} searched / ${markdownNumber(manifest.skipped_session_count)} skipped sessions, confidence ${markdownNumber(manifest.confidence)}`);
    }
  }
  lines.push("");
  lines.push("## Operating Readout");
  lines.push("");
  lines.push(`### ${report.mirror.headline}`);
  lines.push("");
  lines.push(report.mirror.body);
  lines.push("");
  for (const claim of report.mirror.claims) {
    lines.push(`- ${markdownText(claim.text, "Claim retained for review")} (${markdownStrings(claim.evidence_refs).join(", ") || "evidence refs pending"})`);
  }
  lines.push("");
  lines.push("## Operating Leakage");
  lines.push("");
  if (report.tension_metrics.length === 0) {
    lines.push("- No operating leakage metrics reached the public-summary threshold.");
  } else {
    for (const metric of report.tension_metrics) {
      lines.push(`- ${markdownText(metric.value, "0")} ${markdownText(metric.label, "signal")}: ${markdownText(metric.explanation, "Evidence retained for review.")}`);
    }
  }
  lines.push("");
  lines.push("## Evidence Paths");
  lines.push("");
  for (const trail of markdownPublicTrails(report).slice(0, 12)) {
    lines.push(`- [${markdownText(trail.kind, "trail")}] ${markdownPublicTitle(trail.title, "Work trail")} - ${markdownText(trail.state, "observed")}, ${markdownText(trail.valence, "neutral")}, ${markdownText(trail.shape, "single_signal")} (${markdownStrings(trail.evidence_refs).join(", ") || "evidence refs pending"})`);
  }
  lines.push("");
  lines.push("## Recurring Patterns");
  lines.push("");
  if (report.recurring_patterns.length === 0) {
    lines.push("- No recurring patterns detected yet.");
  } else {
    for (const pattern of report.recurring_patterns) {
      lines.push(`- [${markdownText(pattern.severity, "medium")}] ${markdownPublicTitle(pattern.title, "Recurring work pattern")}: ${markdownText(pattern.root_cause_hypothesis, "The same work shape is recurring without a durable repair.")}`);
    }
  }
  lines.push("");
  lines.push("## Top Findings");
  lines.push("");
  for (const finding of report.findings.slice(0, 12)) {
    lines.push(`- [${markdownText(finding.type, "finding")}] ${markdownPublicTitle(finding.title, "Work finding")} (${markdownText(finding.evidence_ref, "evidence ref pending")})`);
  }
  lines.push("");
  lines.push("## Missed Orchestration");
  lines.push("");
  if (report.missed_orchestration_opportunities.length === 0) {
    lines.push("- No missed orchestration opportunities detected.");
  } else {
    for (const finding of report.missed_orchestration_opportunities) {
      lines.push(`- ${finding.title}: ${finding.summary}`);
    }
  }
  lines.push("");
  lines.push("## Initiative Kickoffs");
  lines.push("");
  for (const kickoff of report.initiative_kickoffs) {
    lines.push(`- [${kickoff.priority}] ${kickoff.title}: ${kickoff.summary}`);
  }
  lines.push("");
  lines.push("## Repair Recommendations");
  lines.push("");
  for (const recommendation of report.recommendations) {
    lines.push(`- [${recommendation.priority}] ${recommendation.title}: ${recommendation.summary}`);
  }
  lines.push("");
  lines.push("## Attribution Spine");
  lines.push("");
  lines.push(`Source events: ${report.attribution_spine.source_events.length}`);
  lines.push(`Evidence refs: ${report.attribution_spine.evidence_refs.length}`);
  lines.push(`Review nodes: ${report.attribution_spine.review.pending_count}`);
  lines.push(`Confidence: ${report.attribution_spine.confidence}`);
  lines.push("");
  const attributionGroups: Array<[string, WorkGraphAttributionNode[]]> = [
    ["Actions", report.attribution_spine.actions],
    ["Decisions", report.attribution_spine.decisions],
    ["Artifacts", report.attribution_spine.artifacts],
    ["People", report.attribution_spine.people],
    ["Businesses", report.attribution_spine.businesses],
    ["Surfaces", report.attribution_spine.product_surfaces],
    ["Sources", report.attribution_spine.sources],
  ];
  for (const [label, nodes] of attributionGroups) {
    if (nodes.length === 0) continue;
    lines.push(`${label}:`);
    for (const node of nodes.slice(0, 5)) {
      lines.push(`- ${node.label} (${node.evidence_refs.join(", ") || "source coverage"})`);
    }
    lines.push("");
  }
  lines.push("## Signup Hydration");
  lines.push("");
  lines.push(`- Strategy: ${report.signup_hydration.strategy}`);
  lines.push(`- Eligible: ${report.signup_hydration.eligible ? "yes" : "no"}`);
  for (const note of report.signup_hydration.notes) {
    lines.push(`- ${note}`);
  }
  lines.push("");
  lines.push("## Privacy");
  lines.push("");
  lines.push("- Raw transcripts were not sent.");
  lines.push("- This report contains summaries, hashes, and evidence refs only.");

  return lines.join("\n");
}
